import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  TextField,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Checkbox,
  FormControlLabel,
  Tooltip,
  Link,
  Chip,
  Autocomplete,
  Switch,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  Close as CloseIcon,
  OpenInFull as OpenInFullIcon,
  ArrowBack,
  ArrowForward,
  Add as AddIcon,
  Delete as DeleteIcon,
  CheckCircle,
  Chat as ChatIcon,
  ContentCopy as ContentCopyIcon,
  CloudUpload as CloudUploadIcon,
  InfoOutlined as InfoOutlinedIcon,
  AccountTree as WorkflowCanvasIcon,
} from '@mui/icons-material';
import { Check } from '@mui/icons-material';
import type { StepIconProps } from '@mui/material/StepIcon';
import { modalStyles } from '../styles/modalStyles';
import { API_CONFIG, API_ENDPOINTS, STORAGE_KEYS, SOCKET_KEYS, GMAIL_CLIENT_ID, GCHAT_INBOX_CLIENT_ID, OFFICE365_CLIENT_ID, EMAIL_API_ENDPOINTS, MESSAGING_API_ENDPOINTS, SHOW_GMAIL_FEATURES, SHOW_OUTLOOK_FEATURES, DISABLE_SUBSCRIPTION, SUBSCRIPTION_LIMIT_MESSAGES } from '../helpers/constants';
import { resolveDefaultWorkspace } from '../helpers/defaultWorkspace';
import { getLoggedInUserData } from '../helpers/storageUtils';
import { validateSubscription } from '../services/subscriptionService';
import { getOffice365AdminConsentUrl } from '../helpers/office365AdminConsent';
import { LanguageConstants } from '../helpers/LanguageConstants';
import AppsSelectionStep from './AddSpaceModal/AppsSelectionStep';
import SkillsSelectionStep from './AddSpaceModal/SkillsSelectionStep';
import DataSourceStep from './AddSpaceModal/DataSourceStep';
import ChannelTypeSelectionView from './AddSpaceModal/ChannelTypeSelectionView';
import AddSignatureAndSalutationModal, { SignatureAndSalutationData } from './AddSignatureAndSalutationModal';
import WorkspaceTagAutocomplete, { type WorkspaceTagAutocompleteHandle } from './WorkspaceTagAutocomplete';
import DescriptionCards from './AddSpaceModal/DescriptionCards';
import { useSpamAutoPushToggle, persistSpamAutoPushSetting } from './AddSpaceModal/useSpamAutoPushToggle';
import BusinessRulesSection from './ChannelSettings/BusinessRulesSection';
import WorkflowCanvasPreviewDialog from './ChannelSettings/WorkflowCanvas/WorkflowCanvasPreviewDialog';
import type { BusinessRulesValidationResult } from '../services/businessRulesService';
import AutomationTab from './ChannelSettings/AutomationTab';
import { NO_SUBTAGS_EMPTY_STATE_EMAIL_SETUP } from './ChannelSettings/automationTabConstants';
import KeywordsMultiSelect from './KeywordsMultiSelect';
import { capitalizeFirstLetter } from '../helpers/stringUtils';
import socketService from '../services/socketService';
import { createSignatureSalutation, CreateSignatureSalutationRequest, connectEmailImap, ConnectEmailImapRequest, deleteEmailData, generateDescription } from '@/services/emailService';
import { connectMessaging, ConnectMessagingRequest, connectGoogleChatInbox, ConnectGoogleChatInboxRequest } from '@/services/messagingService';
import { saveChannelBusinessRules } from '@/services/businessRulesService';
import channelService from '../services/channelService';
import { encryptSecretForStorage } from '../services/secretEncryptionService';
import { useDescriptionHandlers } from '../hooks/useDescriptionHandlers';
import { useToast } from '../contexts/ToastContext';
import { measureScrollbarWidth } from '../helpers/domUtils';
import { persistChannelDescriptionAndRules } from '../helpers/channelPersistenceUtils';
import { getRuntimeEnv } from '../config/runtimeConfig';

const hiddenMessagingProviders = new Set(
  (getRuntimeEnv().VITE_HIDDEN_MESSAGING_PROVIDERS ?? '').split(',').map(s => s.trim()).filter(Boolean)
);

const TAG_OPTIONS = [
  'Sales Support', 'Customer Support', 'Business Operations Support',
  'Accounts', 'Billing', 'HR', 'Recruitment', 'IT support', 'Others',
];

interface AddSpaceModalProps {
  open: boolean;
  onClose: () => void;
  onSpaceCreated?: () => void; // Callback to refresh channels list
}

interface SpaceData {
  name: string;
  description: string;
  workspaceTag: string;
  workspaceId: string;
  selectedApps: string[];
  dataSources: any[];
  signatureAndSalutation: SignatureAndSalutationData[];
}

const DESCRIPTION_EXPAND_ICON_BASE_RIGHT = 8;
const DESCRIPTION_EXPAND_ICON_SCROLL_GAP = 8;

const EMPTY_MESSAGING_CREDENTIALS = {
  whatsapp: { access_token: '', phone_number_id: '', sync_interval: 30, sync_interval_unit: 'seconds' as 'seconds' | 'minutes' | 'hours' },
  slack: { bot_token: '', workspace_id: '' },
  google_chat: { service_account_key: '', space_id: '' },
  // DM-only, OAuth2 real-mailbox provider — distinct from google_chat above (named space,
  // service-account). access_token/refresh_token are populated by the OAuth popup flow, not typed
  // in by the user. See docs/GoogleChatInbox_DM_FE_Implementation.md.
  google_chat_inbox: { mailbox_email: '', access_token: '', refresh_token: '' },
  teams: { client_id: '', client_secret: '', tenant_id: '' },
};

const AddSpaceModal: React.FC<AddSpaceModalProps> = ({ open, onClose, onSpaceCreated }) => {
  const { showWarning, showSuccess, showError } = useToast();
  // PR #662 review [L1]: extracted into its own hook (AddSpaceModal/useSpamAutoPushToggle.ts) so
  // its pre-creation/post-creation/success/failure behavior is covered by a focused unit test
  // instead of needing to render this entire (very large) wizard component.
  const {
    spamAutoPushOnCreate,
    isPersistingSpamAutoPush,
    handleSpamAutoPushToggle,
  } = useSpamAutoPushToggle(showSuccess, showError);
  const muiTheme = useTheme();
  // Channel type selection state
  const [selectedChannelType, setSelectedChannelType] = useState<'email' | 'message' | 'voice' | null>(null);
  const [showChannelTypeSelection, setShowChannelTypeSelection] = useState<boolean>(true);
  const [showStepCreation, setShowStepCreation] = useState<boolean>(false);
  // When subscription is disabled, block add-channel flow if validation API denies permission
  const [subscriptionBlocked, setSubscriptionBlocked] = useState<boolean>(false);
  const [subscriptionValidationLoading, setSubscriptionValidationLoading] = useState<boolean>(false);
  const [channelName, setChannelName] = useState<string>('');
  const [voiceChannelNumber, setVoiceChannelNumber] = useState<string>('');
  // Messaging provider state (must be declared before getSteps)
  const [selectedMessagingProvider, setSelectedMessagingProvider] = useState<'regular' | 'whatsapp' | 'slack' | 'google_chat' | 'google_chat_inbox' | 'teams' | null>(null);
  const [persistedMessagingCredentials, setPersistedMessagingCredentials] = useState<typeof EMPTY_MESSAGING_CREDENTIALS>({ ...EMPTY_MESSAGING_CREDENTIALS });
  const [whatsappLoading, setWhatsappLoading] = useState(false);
  const [messagingConnectionData, setMessagingConnectionData] = useState<any>(null);
  
  // Messaging provider modal state
  const [messagingProviderModalOpen, setMessagingProviderModalOpen] = useState(false);
  // True while the merged "Google Chat" dialog is showing the Space-vs-Direct-Messages picker,
  // before selectedProviderForModal has resolved to either 'google_chat' or 'google_chat_inbox'.
  const [googleChatPickerOpen, setGoogleChatPickerOpen] = useState(false);
  const [selectedProviderForModal, setSelectedProviderForModal] = useState<'whatsapp' | 'slack' | 'google_chat' | 'google_chat_inbox' | 'teams' | null>(null);
  const [gchatKeyFileName, setGchatKeyFileName] = useState<string>('');
  const gchatFileInputRef = useRef<HTMLInputElement>(null);
  const [messagingProviderCredentials, setMessagingProviderCredentials] = useState<any>({
    whatsapp: { access_token: '', phone_number_id: '', sync_interval: 30, sync_interval_unit: 'seconds' },
    slack: { bot_token: '', workspace_id: '' },
    google_chat: { service_account_key: '', space_id: '' },
    google_chat_inbox: { mailbox_email: '', access_token: '', refresh_token: '' },
    teams: { client_id: '', client_secret: '', tenant_id: '' }
  });
  const [testingMessagingConnection, setTestingMessagingConnection] = useState(false);
  const [messagingConnectionTested, setMessagingConnectionTested] = useState(false);
  // Google Chat Inbox OAuth popup state — separate from Gmail's (gmailLoading/oauthData below),
  // see docs/GoogleChatInbox_DM_FE_Implementation.md §5.5 for why this must not share state with
  // the email OAuth flow.
  const [gchatInboxOauthLoading, setGchatInboxOauthLoading] = useState(false);
  const [messagingTestSuccessData, setMessagingTestSuccessData] = useState<any>(null);

  // Get steps based on channel type
  const getSteps = () => {
    if (!selectedChannelType) return [];
    
    if (selectedChannelType === 'email') {
      return [
        'Email Connection',
        'Signature & Salutation',
        LanguageConstants.datasource_s,
        'Connect Apps',
        'Connect Skills',
        'Automation',
      ];
    } else if (selectedChannelType === 'message') {
      // Step 0: Messaging Provider (provider, workspace, description, business rules)
      // Step 1: Data Sources
      // Step 2: Connect Apps
      // Step 3: Connect Skills
      return [
        'Messaging Provider',
        LanguageConstants.datasource_s,
        'Connect Apps',
        'Connect Skills',
      ];
    } else if (selectedChannelType === 'voice') {
      return ['Voice Channel Number', LanguageConstants.datasource_s, 'Connect Apps'];
    }
    return [];
  };

  const steps = getSteps();

  const [activeStep, setActiveStep] = useState(0);
  const [spaceData, setSpaceData] = useState<SpaceData>({
    name: '',
    description: '',
    workspaceTag: '',
    workspaceId: '',
    selectedApps: [],
    dataSources: [],
    signatureAndSalutation: [],
  });
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dataSourceLoading, setDataSourceLoading] = useState(false); // New loading state for ${LanguageConstants.datasource_s.toLowerCase()}
  const [error, setError] = useState<string | null>(null);
  const [createdChannelId, setCreatedChannelId] = useState<string>('');
  // Odoo #17929 — channel auto-creates as soon as business-rules validation succeeds (see the
  // effect below), instead of waiting for the "Next" click. "See Workflow" pops up a preview of
  // the parsed canvas without leaving the onboarding wizard.
  const [showWorkflowPreview, setShowWorkflowPreview] = useState(false);
  const [showNewTagInput, setShowNewTagInput] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [selectedEmailProvider, setSelectedEmailProvider] = useState<'gmail' | 'office365' | 'other' | null>(null);
  const [otherEmailCredentials, setOtherEmailCredentials] = useState({
    emailId: '',
    password: '',
    imapServer: '',
    imapPort: '993',
    imapSsl: 'yes',
    imapSecurity: 'ssl',
    smtpServer: '',
    smtpPort: '587',
    smtpAuth: 'yes',
    smtpSsl: 'yes',
    smtpSecurity: 'tls'
  });
  const [connectionTested, setConnectionTested] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [gmailLoading, setGmailLoading] = useState(false);
  const [office365Loading, setOffice365Loading] = useState(false);
  const [adminConsentDialogOpen, setAdminConsentDialogOpen] = useState(false);
  const [adminConsentCopySuccess, setAdminConsentCopySuccess] = useState(false);
  const [oauthData, setOauthData] = useState<any>(null);

  // Description suggestions state (for email channels only)
  const [descriptionSuggestions, setDescriptionSuggestions] = useState<string[]>([]);
  const [loadingDescriptions, setLoadingDescriptions] = useState(false);
  const [showDescriptionField, setShowDescriptionField] = useState(false);
  const [showDescriptionCards, setShowDescriptionCards] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [descriptionHasVerticalScroll, setDescriptionHasVerticalScroll] = useState(false);
  const [descriptionScrollbarWidth, setDescriptionScrollbarWidth] = useState(0);
  
  // Per-tag subtags state: each category owns its own list of subtags
  const [selectedKeywordsPerTag, setSelectedKeywordsPerTag] = useState<Record<string, string[]>>({});
  // Categories state (for email channels only) - predefined tags
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  // tagRows: each row has a stable id (for React key) and the selected tag name
  const [tagRows, setTagRows] = useState<{ id: string; tag: string }[]>([{ id: crypto.randomUUID(), tag: '' }]);

  // Sync tagRows → selectedCategories (filter out empty slots)
  useEffect(() => {
    setSelectedCategories(tagRows.map(r => r.tag).filter(Boolean));
  }, [tagRows]);

  // Flat deduplicated list of all subtags across all categories — used for description
  // generation and for the backward-compat channel_sub_tags payload until BE supports per-tag format.
  const flatSelectedKeywords = useMemo(
    () => Array.from(new Set(Object.values(selectedKeywordsPerTag).flat())),
    [selectedKeywordsPerTag],
  );

  // Keep selectedKeywordsPerTag in sync when categories change:
  // add an empty array for newly selected categories, drop removed ones (preserving existing subtags).
  useEffect(() => {
    setSelectedKeywordsPerTag((prev) => {
      const next: Record<string, string[]> = {};
      for (const cat of selectedCategories) {
        next[cat] = prev[cat] ?? [];
      }
      return next;
    });
  }, [selectedCategories]);
  const [businessRules, setBusinessRules] = useState<string>('');
  const [isBusinessRulesSuggesting, setIsBusinessRulesSuggesting] = useState(false);
  const [businessRulesValidationStatus, setBusinessRulesValidationStatus] = useState<
    'idle' | 'pending' | 'validating' | 'errors' | 'warnings' | 'clean'
  >('idle');
  const [businessRulesCanProceed, setBusinessRulesCanProceed] = useState<boolean>(false);
  const [businessRulesForceSave, setBusinessRulesForceSave] = useState<boolean>(false);
  const [businessRulesValidationResult, setBusinessRulesValidationResult] =
    useState<BusinessRulesValidationResult | null>(null);
  const [serverValidationResult, setServerValidationResult] =
    useState<BusinessRulesValidationResult | null>(null);
  // Inline field-level message for a plain-string 422 (e.g. "channel_business_rules is required
  // when creating a new channel") — distinct from serverValidationResult, which only carries
  // structured validation_result objects. Cleared whenever the user edits business rules.
  const [businessRulesServerError, setBusinessRulesServerError] = useState<string>('');
  const [debouncedWorkspaceTag, setDebouncedWorkspaceTag] = useState<string>('');
  const [debouncedKeywords, setDebouncedKeywords] = useState<string[]>([]);
  const previousDebouncedKeywordsRef = useRef<string[]>([]);
  const previousDebouncedWorkspaceTagRef = useRef<string>('');
  const previousSelectedCategoriesRef = useRef<string[]>([]);
  
  // Ref to track abort controller for description API calls
  const descriptionAbortControllerRef = useRef<AbortController | null>(null);
  const descriptionRequestIdRef = useRef<number>(0);
  
  // Refs for popup monitoring intervals
  const gmailPopupIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const office365PopupIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const gchatInboxPopupIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // Ref to DataSourceStep component
  const dataSourceStepRef = useRef<any>(null);
  const dialogContentRef = useRef<HTMLDivElement>(null);
  const errorAlertRef = useRef<HTMLDivElement>(null);
  const descriptionSectionRef = useRef<HTMLDivElement>(null);
  const descriptionTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  // Guard: run subscription validation only once per modal open to avoid API loop
  const subscriptionValidationRunRef = useRef<boolean>(false);
  // Ref to blur the Workspace field when opening Connect WhatsApp (or other provider) modal so it does not stay focused
  const messagingWorkspaceInputRef = useRef<WorkspaceTagAutocompleteHandle | null>(null);
  // Only call the default-workspace auto-select lookup once per modal open (avoid loop) — same pattern as subscriptionValidationRunRef above
  const defaultWorkspaceAutoSelectRunRef = useRef<boolean>(false);
  // Odoo #17929 — one-shot guard so the auto-create effect (below) fires createChannel() at most
  // once per modal open even if validation flaps clean -> warnings -> clean. A failed auto-create
  // is NOT retried automatically (avoids looping on a persistent failure e.g. bad OAuth token) —
  // the user's own manual "Next" click always still calls createChannel() directly (existing
  // handleNext logic), independent of this ref, and remains the retry path.
  const autoCreateAttemptedRef = useRef<boolean>(false);

  // Scroll to top when step changes
  useEffect(() => {
    if (dialogContentRef.current) {
      dialogContentRef.current.scrollTop = 0;
    }
  }, [activeStep]);

  const resetBusinessRulesValidation = useCallback(() => {
    setBusinessRulesValidationStatus('idle');
    setBusinessRulesCanProceed(false);
    setBusinessRulesForceSave(false);
    setBusinessRulesValidationResult(null);
    setServerValidationResult(null);
  }, []);

  const getConfiguredEmailId = () =>
    selectedEmailProvider === 'other'
      ? otherEmailCredentials.emailId
      : oauthData?.email_id || '';

  const getBusinessRulesSaveExtras = () =>
    businessRules.trim()
      ? {
          channel_business_rules: businessRules.trim(),
          ...(businessRulesValidationResult
            ? { validation_result: businessRulesValidationResult }
            : {}),
          ...(businessRulesForceSave ? { force_save: true } : {}),
        }
      : {};

  // persistSpamAutoPushSetting and handleSpamAutoPushToggle now live in
  // AddSpaceModal/useSpamAutoPushToggle.ts (see the useSpamAutoPushToggle() call above) —
  // PR #662 review [L1]: extracted so this toggle's behavior can be unit-tested without rendering
  // this entire component. persistSpamAutoPushSetting is imported directly for the two
  // createChannel() call sites below, which apply the pending toggle value at creation time.

  const hasBlockingBusinessRulesValidation = () =>
    selectedChannelType === 'email' &&
    activeStep === 0 &&
    businessRules.trim() !== '' &&
    (!businessRulesCanProceed ||
      businessRulesValidationStatus === 'pending' ||
      businessRulesValidationStatus === 'validating');

  // Business rules UI only appears on email step 0; scope Next-button feedback to match.
  const isStep0EmailRulesValidating = useMemo(
    () =>
      selectedChannelType === 'email' &&
      activeStep === 0 &&
      businessRules.trim() !== '' &&
      businessRulesValidationStatus === 'validating',
    [selectedChannelType, activeStep, businessRules, businessRulesValidationStatus],
  );

  const applyServerValidationResult = (serverResult: BusinessRulesValidationResult) => {
    setServerValidationResult(serverResult);
    setBusinessRulesValidationResult(serverResult);
    setBusinessRulesValidationStatus(serverResult.status);
    setBusinessRulesCanProceed(
      serverResult.status === 'clean' || serverResult.status === 'warnings',
    );
    setBusinessRulesForceSave(serverResult.status === 'warnings');
  };

  const handleBusinessRulesValidationMetaChange = useCallback((meta: {
    status: 'idle' | 'pending' | 'validating' | 'errors' | 'warnings' | 'clean';
    canUpdate: boolean;
    forceSave: boolean;
    result: BusinessRulesValidationResult | null;
  }) => {
    setBusinessRulesValidationStatus(meta.status);
    setBusinessRulesCanProceed(meta.canUpdate);
    setBusinessRulesForceSave(meta.forceSave);
    setBusinessRulesValidationResult(meta.result);
    if (meta.status !== 'errors') {
      setServerValidationResult(null);
    }
  }, []);

  // Odoo #17929 — auto-create the channel as soon as business-rules validation resolves to
  // clean/warnings, instead of waiting for the explicit "Next" click. advanceStep: false keeps
  // the user on step 0 so the "See Workflow" button (rendered once createdChannelId is set) is
  // reachable before they move on. Voice channels are unaffected — they still create via
  // handleNext's existing createChannel() call (advanceStep defaults to true there).
  // See_workflow_tab_button_is_not_shown_in_message_channel — extended to 'message' so that
  // channel type gets the exact same auto-create-then-pause behavior as email (previously
  // email-only; messaging required an explicit "Next" click, which immediately advanced past
  // step 0 before the admin could ever see the "See Workflow" button). Note: unlike email (whose
  // business-rules section can't render before OAuth/IMAP connects), messaging's Description field
  // isn't actually disabled until a provider is picked — so this effect COULD in theory fire with
  // no selectedMessagingProvider yet, if an admin types rules before choosing one. createChannel()'s
  // messaging branch only matches a known provider value, and throws a clear "credentials are
  // missing" error for WhatsApp/Google Chat if a provider was chosen but not yet tested — either
  // way it fails gracefully via its own try/catch (setError), autoCreateAttemptedRef prevents a
  // retry loop, and the admin can still finish via the manual "Next" fallback below, which re-checks
  // readiness itself.
  useEffect(() => {
    if ((selectedChannelType !== 'email' && selectedChannelType !== 'message') || activeStep !== 0) return;
    if (createdChannelId) return;
    if (!businessRulesCanProceed || businessRules.trim() === '') return;
    if (autoCreateAttemptedRef.current || loading) return;
    autoCreateAttemptedRef.current = true;
    void createChannel({ advanceStep: false });
    // PR #650 review (MEDIUM) — createChannel is intentionally not in the dep array: it's a
    // non-memoized function recreated every render, so listing it would re-run this effect on
    // every render too (wastefully, though not incorrectly — the ref guard above already
    // prevents a double-create regardless of which render's closure fires). The guard sets
    // autoCreateAttemptedRef.current synchronously before calling createChannel, not inside its
    // callback, so it's immune to which closure ends up executing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChannelType, activeStep, createdChannelId, businessRulesCanProceed, businessRules, loading]);

  const resetDescriptionScrollState = useCallback(() => {
    setDescriptionHasVerticalScroll(false);
    setDescriptionScrollbarWidth(0);
  }, []);

  const updateDescriptionScrollState = useCallback(() => {
    const el = descriptionTextareaRef.current;
    if (!el) return;
    const hasScroll = el.scrollHeight > el.clientHeight + 1;
    const scrollbarWidth = measureScrollbarWidth(el, hasScroll);
    setDescriptionHasVerticalScroll(hasScroll);
    setDescriptionScrollbarWidth(scrollbarWidth);
  }, []);

  useEffect(() => {
    const shouldMeasureDescriptionScroll =
      open &&
      selectedChannelType === 'email' &&
      activeStep === 0 &&
      showDescriptionField &&
      !showDescriptionCards;

    if (!shouldMeasureDescriptionScroll) {
      resetDescriptionScrollState();
      return;
    }

    const frame = requestAnimationFrame(updateDescriptionScrollState);
    return () => cancelAnimationFrame(frame);
  }, [
    open,
    selectedChannelType,
    activeStep,
    showDescriptionField,
    showDescriptionCards,
    spaceData.description,
    resetDescriptionScrollState,
    updateDescriptionScrollState,
  ]);

  useEffect(() => {
    const shouldMeasureDescriptionScroll =
      open &&
      selectedChannelType === 'email' &&
      activeStep === 0 &&
      showDescriptionField &&
      !showDescriptionCards;

    if (!shouldMeasureDescriptionScroll) {
      resetDescriptionScrollState();
      return;
    }

    let observer: ResizeObserver | null = null;
    const frame = requestAnimationFrame(() => {
      const el = descriptionTextareaRef.current;
      if (!el) return;

      updateDescriptionScrollState();
      observer = new ResizeObserver(updateDescriptionScrollState);
      observer.observe(el);
    });

    return () => {
      cancelAnimationFrame(frame);
      observer?.disconnect();
    };
  }, [
    open,
    selectedChannelType,
    activeStep,
    showDescriptionField,
    showDescriptionCards,
    resetDescriptionScrollState,
    updateDescriptionScrollState,
  ]);

  useEffect(() => {
    if (!open || selectedChannelType !== 'email' || activeStep !== 0) {
      setIsDescriptionExpanded(false);
    }
  }, [open, selectedChannelType, activeStep]);

  // Clear error when modal opens; when subscription disabled, run validation on open once (avoid loop)
  useEffect(() => {
    if (open) {
      setError(null);
      setSubscriptionBlocked(false);
      // Only call API once per open session; skip if already ran (e.g. strict mode or remount)
      if (DISABLE_SUBSCRIPTION && !subscriptionValidationRunRef.current) {
        subscriptionValidationRunRef.current = true;
        setSubscriptionValidationLoading(true);
        const userData = getLoggedInUserData();
        const userId = userData?.user_id || '';
        validateSubscription(userId)
          .then((validation) => {
            // Only explicit true allows channel creation; false or null shows limit message
            if (!validation.canCreateChannel) {
              setError(SUBSCRIPTION_LIMIT_MESSAGES.CHANNEL);
              setSubscriptionBlocked(true);
            }
          })
          .finally(() => setSubscriptionValidationLoading(false));
      }
    } else {
      setSubscriptionBlocked(false);
      subscriptionValidationRunRef.current = false;
      autoCreateAttemptedRef.current = false;
      setShowWorkflowPreview(false);
    }
  }, [open]);

  // Email/message channels default to the current company's own workspace instead of showing
  // the "What workspace group does this channel belong to?" picker (that field is still
  // rendered, unchanged, for voice channel creation). Calls the same GET /channels/tags
  // endpoint the picker itself uses, with NO search/name filter — under the backend's default
  // configuration that endpoint already scopes results to the logged-in user's own company.
  // handleWorkspaceTagChange(...) is the exact same handler a manual WorkspaceTagAutocomplete
  // pick would call, so workspace_id/workspace_tag are populated exactly as before — no new
  // payload logic. Runs once per modal open (ref reset below), not on every render/step change.
  //
  // makind_workspace_as_default branch — resolution order (PR #634 review fallout: a hardcoded
  // "Giggso Inc" name-search, then a blind `tags[0]`, both broke for any company that wasn't the
  // one they happened to be tested against):
  //   1. Fetch the accessible workspace list (below).
  //   2. The user's profile default_workspace_id, if it matches one of them.
  //   3. Otherwise, a workspace whose description mentions "default workspace".
  //   4. Otherwise, stop and show a clear error — never guess (see resolveDefaultWorkspace).
  useEffect(() => {
    if (
      !open ||
      defaultWorkspaceAutoSelectRunRef.current ||
      (selectedChannelType !== 'email' && selectedChannelType !== 'message')
    ) {
      return;
    }
    defaultWorkspaceAutoSelectRunRef.current = true;

    (async () => {
      try {
        const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
        const url = `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.CHANNELS.TAGS(1, 15)}`;
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            accept: 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        const tags: Array<{ id: string; name: string; description?: string | null }> = data?.tags || [];

        // Step 2 — the user's profile default_workspace_id, when present. Not every login flow
        // populates this yet (only email verification currently writes it), so both known
        // sources are checked defensively; either being absent just means step 3 decides instead.
        let profileDefaultWorkspaceId: string | null = null;
        try {
          const loggedInUserData = localStorage.getItem(STORAGE_KEYS.LOGGED_IN_USER_DATA);
          profileDefaultWorkspaceId = (loggedInUserData && JSON.parse(loggedInUserData)?.default_workspace_id) || null;
        } catch {
          // Malformed/missing profile blob — fall through to the plain localStorage key below.
        }
        if (!profileDefaultWorkspaceId) {
          profileDefaultWorkspaceId = localStorage.getItem('defaultWorkspaceId');
        }

        const defaultTag = resolveDefaultWorkspace(tags, profileDefaultWorkspaceId);
        if (defaultTag) {
          handleWorkspaceTagChange({ id: defaultTag.id, name: defaultTag.name });
        } else {
          setError('No default workspace configured for your company. Please contact support.');
        }
      } catch (err) {
        setError('Failed to load your company\'s default workspace. Please try again.');
      }
    })();
  }, [open, selectedChannelType]);

  // Reset the auto-select-once guard when the modal closes so it runs again next time it opens
  useEffect(() => {
    if (!open) {
      defaultWorkspaceAutoSelectRunRef.current = false;
    }
  }, [open]);

  // Scroll to top when error appears
  useEffect(() => {
    if (error && dialogContentRef.current) {
      // Small delay to ensure the Alert is rendered
      setTimeout(() => {
        if (dialogContentRef.current) {
          // Scroll to the top of the dialog content
          dialogContentRef.current.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }, [error]);

  // Auto-scroll to description section when loading starts (only on initial load, not on tag changes)
  useEffect(() => {
    // Only auto-scroll if we're starting fresh (no suggestions yet) to prevent scroll on tag changes
    if (loadingDescriptions && descriptionSuggestions.length === 0 && descriptionSectionRef.current && dialogContentRef.current) {
      // Small delay to ensure the description section is rendered
      const timeoutId = setTimeout(() => {
        if (descriptionSectionRef.current && dialogContentRef.current) {
          const descriptionSectionTop = descriptionSectionRef.current.offsetTop;
          const dialogContent = dialogContentRef.current;
          const scrollPosition = descriptionSectionTop - 20; // Add some padding from top
          
          dialogContent.scrollTo({
            top: scrollPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [loadingDescriptions, descriptionSuggestions.length]);


  // Ref to show cancellation toast only once (avoids duplicate toasts from duplicate postMessage or listeners)
  const oauthCancelledToastShownRef = useRef(false);
  // Listen for OAuth data and cancellation from callback pages (e.g. Gmail SSO popup)
  useEffect(() => {
    const handleOAuthData = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;

      // Google Chat Inbox OAuth — MUST be checked before the generic OAUTH_SUCCESS branch below
      // and MUST return early. That branch unconditionally calls setOauthData(), which feeds the
      // EMAIL connect path in createChannel() — without this early return, completing the Chat
      // OAuth popup would be silently swallowed into the email-connect flow instead of the
      // intended Chat Inbox one. See docs/GoogleChatInbox_DM_FE_Implementation.md §5.5.
      if (event.data?.type === 'OAUTH_SUCCESS' && event.data.data?.provider === 'google_chat_inbox') {
        const tokenData = event.data.data;
        setGchatInboxOauthLoading(false);
        if (gchatInboxPopupIntervalRef.current) {
          clearInterval(gchatInboxPopupIntervalRef.current);
          gchatInboxPopupIntervalRef.current = null;
        }

        // Mailbox address comes entirely from whichever account was chosen in the OAuth popup —
        // same pattern as Gmail's connect flow (oauthData.email_id). No pre-typed field to
        // validate against; the backend's own authenticate() independently re-verifies this
        // identity at connect time regardless.
        const connectedCredentials = {
          mailbox_email: tokenData.email_id || '',
          access_token: tokenData.access_token,
          refresh_token: tokenData.refresh_token,
        };

        setMessagingProviderCredentials((prev: any) => ({
          ...prev,
          google_chat_inbox: connectedCredentials,
        }));
        setMessagingConnectionTested(true);
        setMessagingTestSuccessData({ success: true, provider: 'google_chat_inbox' });
        setSelectedMessagingProvider('google_chat_inbox');
        setPersistedMessagingCredentials((prev) => ({
          ...prev,
          google_chat_inbox: connectedCredentials,
        }));
        setMessagingProviderModalOpen(false);
        setSelectedProviderForModal(null);
        setError(null);
        return;
      }
      if (event.data?.type === 'OAUTH_CANCELLED' && event.data.provider === 'google_chat_inbox') {
        setGchatInboxOauthLoading(false);
        if (gchatInboxPopupIntervalRef.current) {
          clearInterval(gchatInboxPopupIntervalRef.current);
          gchatInboxPopupIntervalRef.current = null;
        }
        return;
      }

      if (event.data && event.data.type === 'OAUTH_SUCCESS') {
        setOauthData(event.data.data);
        setGmailLoading(false);
        setOffice365Loading(false);

        // Clear popup monitoring intervals on successful OAuth
        if (gmailPopupIntervalRef.current) {
          clearInterval(gmailPopupIntervalRef.current);
          gmailPopupIntervalRef.current = null;
        }
        if (office365PopupIntervalRef.current) {
          clearInterval(office365PopupIntervalRef.current);
          office365PopupIntervalRef.current = null;
        }
      }
      // User cancelled OAuth (e.g. Cancel on Google consent) – close popup leaves user on Configure email modal
      // Drive/SharePoint popups include `provider`; those are handled in DataSourceStep/AppsGrid (with toast)
      if (
        event.data &&
        event.data.type === 'OAUTH_CANCELLED' &&
        event.data.provider !== 'google_drive' &&
        event.data.provider !== 'sharepoint' &&
        event.data.provider !== 'google_chat_inbox'
      ) {
        setGmailLoading(false);
        setOffice365Loading(false);
        if (gmailPopupIntervalRef.current) {
          clearInterval(gmailPopupIntervalRef.current);
          gmailPopupIntervalRef.current = null;
        }
        if (office365PopupIntervalRef.current) {
          clearInterval(office365PopupIntervalRef.current);
          office365PopupIntervalRef.current = null;
        }
        if (!oauthCancelledToastShownRef.current) {
          oauthCancelledToastShownRef.current = true;
          // Use warning: user chose to cancel; caution-style feedback
          showWarning('Authentication cancelled by user');
        }
      }
    };

    window.addEventListener('message', handleOAuthData);
    return () => window.removeEventListener('message', handleOAuthData);
  }, [showWarning]);

  // Cleanup intervals on component unmount
  useEffect(() => {
    return () => {
      // Clear popup monitoring intervals
      if (gmailPopupIntervalRef.current) {
        clearInterval(gmailPopupIntervalRef.current);
      }
      if (office365PopupIntervalRef.current) {
        clearInterval(office365PopupIntervalRef.current);
      }
      if (gchatInboxPopupIntervalRef.current) {
        clearInterval(gchatInboxPopupIntervalRef.current);
      }
    };
  }, []);

  // Debounce workspace changes
  useEffect(() => {
    const workspaceTag = showNewTagInput ? newTagName.trim() : spaceData.workspaceTag;
    const timeoutId = setTimeout(() => {
      setDebouncedWorkspaceTag(workspaceTag);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [spaceData.workspaceTag, showNewTagInput, newTagName]);

  // Debounce keywords changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedKeywords(flatSelectedKeywords);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [flatSelectedKeywords]);

  // Track previous activeStep to detect navigation back to step 0
  const previousActiveStepRef = useRef<number>(0);
  
  // Reset description refs when navigating back to step 0 to ensure fresh fetch
  useEffect(() => {
    // When navigating back to step 0 for email channels, reset refs to trigger description refetch
    const wasOnStep1OrHigher = previousActiveStepRef.current > 0;
    const isNowOnStep0 = selectedChannelType === 'email' && activeStep === 0;
    
    if (wasOnStep1OrHigher && isNowOnStep0) {
      // User navigated back from step 1+ to step 0 - reset refs and clear suggestions
      // so description fetching will detect changes and refetch fresh suggestions
      previousDebouncedWorkspaceTagRef.current = '';
      previousDebouncedKeywordsRef.current = [];
      previousSelectedCategoriesRef.current = [];
      // Clear suggestions to ensure fresh fetch when user changes tags
      setDescriptionSuggestions([]);
    }
    
    // Update the previous step ref
    previousActiveStepRef.current = activeStep;
  }, [selectedChannelType, activeStep]);

  // Memoize stable values for dependency array to prevent size changes
  const oauthEmailId = useMemo(() => oauthData?.email_id || '', [oauthData?.email_id]);
  const otherEmailId = useMemo(() => otherEmailCredentials.emailId || '', [otherEmailCredentials.emailId]);
  const stableWorkspaceTag = useMemo(() => debouncedWorkspaceTag || '', [debouncedWorkspaceTag]);
  const stableKeywords = useMemo(() => JSON.stringify(debouncedKeywords), [debouncedKeywords]);
  const stableCategories = useMemo(() => JSON.stringify(selectedCategories), [selectedCategories]);
  const stableDescription = useMemo(() => spaceData.description || '', [spaceData.description]);

  // Fetch description suggestions when email is connected and workspace tag is selected (email channels only)
  useEffect(() => {
    const fetchDescriptions = async () => {
      // Only for email channels
      if (selectedChannelType !== 'email') {
        return;
      }

      // Check if email is connected, workspace is selected, and category tags are selected
      const connectedEmail = selectedEmailProvider === 'other' 
        ? otherEmailId 
        : oauthEmailId;

      // Only proceed if email is connected, workspace, at least one category (tag), and at least one keyword (subtag) are selected
      // Allow description fetching on step 0 even if channel is created (when user navigates back from step 1)
      const isOnEmailConnectionStep = selectedChannelType === 'email' && activeStep === 0;
      // Parse stable categories and keywords for length checks
      const categoriesArray = stableCategories ? JSON.parse(stableCategories) : [];
      const keywordsArray = stableKeywords ? JSON.parse(stableKeywords) : [];
      if (connectedEmail && stableWorkspaceTag && categoriesArray?.length > 0 && keywordsArray?.length > 0 && isOnEmailConnectionStep) {
        // Check if workspace tag, keywords, or categories actually changed
        const workspaceTagChanged = previousDebouncedWorkspaceTagRef.current !== stableWorkspaceTag;
        const keywordsChanged = JSON.stringify(previousDebouncedKeywordsRef.current) !== stableKeywords;
        const categoriesChanged = JSON.stringify(previousSelectedCategoriesRef.current) !== stableCategories;
        
        // Only refetch if workspace, keywords, or categories changed, or if we don't have suggestions yet
        const shouldRefetch = workspaceTagChanged || 
                              keywordsChanged || 
                              categoriesChanged ||
                              descriptionSuggestions.length === 0;

        if (!shouldRefetch) {
          return;
        }

        // Cancel any in-flight requests
        if (descriptionAbortControllerRef.current) {
          descriptionAbortControllerRef.current.abort();
        }

        // Create new abort controller for this request
        const abortController = new AbortController();
        descriptionAbortControllerRef.current = abortController;
        
        // Generate a unique request ID for this request
        const currentRequestId = ++descriptionRequestIdRef.current;
        const hasUserSelectedDescription = spaceData.description.trim() !== '';

        // Update refs to track current values
        previousDebouncedWorkspaceTagRef.current = debouncedWorkspaceTag;
        previousDebouncedKeywordsRef.current = [...debouncedKeywords];
        previousSelectedCategoriesRef.current = [...selectedCategories];

        // Set loading state first to maintain consistent UI height
        // Don't clear suggestions immediately to prevent flicker
        setLoadingDescriptions(true);
        setError(null);
        
        // Show the description section immediately when loading starts (only if user hasn't selected)
        if (!hasUserSelectedDescription && !showDescriptionField && !showDescriptionCards) {
          setShowDescriptionCards(true);
        }
        
        // Keep previous suggestions visible during loading to prevent height changes
        // Only clear if we're starting fresh (no previous suggestions)
        if (descriptionSuggestions.length === 0) {
          // Only clear if we don't have any suggestions yet
        }
        
        try {
          const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
          if (!token) {
            setLoadingDescriptions(false);
            return;
          }

          // Build request payload with categories and tags
          // Categories are required at this point (checked in the if condition above)
          const keywordsArray = stableKeywords ? JSON.parse(stableKeywords) : [];
          const requestPayload = {
            email_id: connectedEmail,
            workspace_name: stableWorkspaceTag,
            categories: categoriesArray, // Categories are required
            tags: keywordsArray, // Tags can be empty array
          };

          const response = await generateDescription(
            requestPayload,
            token,
            abortController.signal
          );

          // Check if this request is still the latest one and wasn't aborted
          if (currentRequestId === descriptionRequestIdRef.current && !abortController.signal.aborted) {
            if (response.status === 'success' && response.descriptions) {
              // Only update suggestions if user hasn't selected a description,
              // or if the selected description is no longer in the new suggestions
              const shouldUpdateSuggestions = !hasUserSelectedDescription || 
                !response.descriptions.some(desc => desc.trim() === stableDescription.trim());
              
              if (shouldUpdateSuggestions) {
                // Update suggestions and loading state together to prevent flicker
                setDescriptionSuggestions(response.descriptions);
                setLoadingDescriptions(false);
                
                // Only auto-show cards if user hasn't selected a description
                if (!hasUserSelectedDescription && !showDescriptionField) {
                  setShowDescriptionCards(true);
                  setShowDescriptionField(false);
                }
                // If user has selected a description, keep showing the field
                // but update the suggestions in the background for when they click "View Suggestions"
              } else {
                // User has selected a description that's still valid, just update suggestions silently
                setDescriptionSuggestions(response.descriptions);
                setLoadingDescriptions(false);
              }
            } else {
              // No descriptions returned, stop loading
              setLoadingDescriptions(false);
            }
          }
        } catch (error: any) {
          // Don't show error if request was aborted (cancelled)
          if (error.name === 'AbortError' || abortController.signal.aborted) {
            // Silently ignore aborted requests - this is expected behavior
            return;
          }
          console.error('Error fetching descriptions:', error);
          // Don't show error to user, just silently fail
          if (currentRequestId === descriptionRequestIdRef.current) {
            setDescriptionSuggestions([]);
          }
        } finally {
          // Only update loading state if this is still the latest request
          if (currentRequestId === descriptionRequestIdRef.current) {
            setLoadingDescriptions(false);
          }
        }
      } else {
        // Reset when conditions are not met (but only if we don't have a description already)
        if (!spaceData.description.trim()) {
          setDescriptionSuggestions([]);
          setShowDescriptionCards(false);
          setShowDescriptionField(false);
        }
        setLoadingDescriptions(false);
        // Reset refs when conditions are not met
        previousDebouncedWorkspaceTagRef.current = '';
        previousDebouncedKeywordsRef.current = [];
        previousSelectedCategoriesRef.current = [];
        // Cancel any in-flight requests
        if (descriptionAbortControllerRef.current) {
          descriptionAbortControllerRef.current.abort();
          descriptionAbortControllerRef.current = null;
        }
      }
    };

    // Add a small delay to avoid too many API calls
    const timeoutId = setTimeout(fetchDescriptions, 500);
    return () => {
      clearTimeout(timeoutId);
      // Cancel request on cleanup
      if (descriptionAbortControllerRef.current) {
        descriptionAbortControllerRef.current.abort();
        descriptionAbortControllerRef.current = null;
      }
    };
  }, [
    selectedChannelType,
    selectedEmailProvider,
    oauthEmailId, // Use memoized value
    otherEmailId, // Use memoized value
    stableWorkspaceTag, // Use memoized value
    stableKeywords, // Use memoized stringified array
    stableCategories, // Use memoized stringified array
    activeStep, // Add activeStep to track when we're on step 0
    descriptionSuggestions.length,
    showDescriptionField,
    stableDescription, // Use memoized value
    // Removed showDescriptionCards from dependencies to prevent refetching when toggling views
    // But kept showDescriptionField to handle the auto-show logic
    // Removed createdChannelId from dependencies since we now check activeStep instead
  ]);

  // Use shared hook for description selection and editing handlers
  const {
    handleDescriptionSelect,
    handleDescriptionEdit,
  } = useDescriptionHandlers({
    onDescriptionChange: (description: string) => {
      setSpaceData(prev => ({ ...prev, description }));
    },
    showDescriptionField,
    setShowDescriptionField,
    setShowDescriptionCards,
    disabled: false, // Not used for select/edit handlers
  });

  // Custom toggle handler with step-specific logic and ref reset functionality
  const handleToggleDescriptionView = () => {
    // Allow toggling on step 0 even if channel is created (when user navigates back from step 1)
    const isOnEmailConnectionStep = selectedChannelType === 'email' && activeStep === 0;
    if (!isOnEmailConnectionStep) {
      return;
    }
    
    if (showDescriptionField) {
      // Switch to cards view - show suggestions
      setShowDescriptionField(false);
      setShowDescriptionCards(true);
      // If we have no suggestions and not loading, trigger a refetch by resetting refs
      // This will cause the useEffect to detect a change and refetch descriptions
      if (descriptionSuggestions.length === 0 && !loadingDescriptions) {
        // Reset refs to force the useEffect to detect changes and refetch
        previousDebouncedWorkspaceTagRef.current = '';
        previousDebouncedKeywordsRef.current = [];
        previousSelectedCategoriesRef.current = [];
      }
    } else {
      // Switch to input field view
      setShowDescriptionField(true);
      setShowDescriptionCards(false);
    }
  };




  // Step icon: white until completed; turns green when completed
  const CustomStepIcon = (props: StepIconProps) => {
    const { active, completed, icon, className } = props;
    const primaryBlue = '#1cabde';
    const successGreen = '#4caf50';
    const isInactive = !active && !completed;
    return (
      <Box
        className={className}
        sx={{
          width: 24,
          height: 24,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: completed ? successGreen : isInactive ? '#ffffff' : primaryBlue,
          color: completed ? '#ffffff' : isInactive ? '#9e9e9e' : '#ffffff',
          border: '2px solid',
          borderColor: completed ? successGreen : isInactive ? '#bdbdbd' : primaryBlue,
          boxShadow: active && !completed ? '0 0 0 2px rgba(28,171,222,0.12)' : 'none',
          fontSize: 12,
          transition: 'all 0.2s ease-in-out',
        }}
      >
        {completed ? <Check sx={{ fontSize: 16 }} /> : icon}
      </Box>
    );
  };

  const handleWorkspaceTagChange = (value: { id: string | null; name: string | null } | null) => {
    if (value && value.id && value.name) {
      setShowNewTagInput(false);
      setNewTagName('');
      setSpaceData(prev => ({ 
        ...prev, 
        workspaceTag: value.name || '',
        workspaceId: value.id || ''
      }));
    } else {
      setSpaceData(prev => ({ ...prev, workspaceTag: '', workspaceId: '' }));
    }
  };

  const handleAddNewWorkspaceTag = () => {
    setShowNewTagInput(true);
    setSpaceData(prev => ({ ...prev, workspaceTag: '', workspaceId: '' }));
  };

  const handleNewTagNameChange = (value: string) => {
    setNewTagName(value);
    setSpaceData(prev => ({ ...prev, workspaceTag: value }));
  };

  const createSignatureAndSalutation = async () => {
    // Check if signatureAndSalutation array exists and has data
    if (!spaceData.signatureAndSalutation || spaceData.signatureAndSalutation.length === 0) {
      // No signature/salutation data, just proceed to next step
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
      return;
    }

    // Get the first signature/salutation entry
    const sigData = spaceData.signatureAndSalutation[0];
    
    // Check if sigData exists and is valid
    if (!sigData) {
      // No valid signature/salutation data, just proceed to next step
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
      return;
    }
    
    // Check if there's at least salutation or signature (name is optional)
    const hasSalutation = sigData?.salutation?.trim() !== '';
    const hasSignature = sigData?.signature && !isRichTextEmpty(sigData.signature);
    
    // If neither salutation nor signature is provided, just proceed to next step
    if (!hasSalutation && !hasSignature) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
      return;
    }

    const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
    
    if (!token) {
      setError('Authentication required. Please log in again.');
      return;
    }

    setLoading(true);
    
    try {
      const apiData: CreateSignatureSalutationRequest = {
        channel_id: createdChannelId,
        name: sigData?.name?.trim() || '', // Handle optional name field
        signature: sigData?.signature || '',
        salutation: sigData?.salutation || '',
        is_default: true,
      };

      const response = await createSignatureSalutation(apiData, token);
      
      if (response.success) {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
      } else {
        setError(response.message || 'Failed to save signature and salutation');
      }
    } catch (error) {
      console.error('Error saving signature and salutation:', error);
      setError(error instanceof Error ? error.message : 'Failed to save signature and salutation');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = async () => {
    // Handle step 0 for message channels (provider selection + workspace + description)
    if (selectedChannelType === 'message' && activeStep === 0) {
      const existingChannelId = createdChannelId || messagingConnectionData?.channel_id || '';
      // Only create channel if it hasn't been created yet
      if (!existingChannelId) {
        // channel_business_rules is required by the backend on new-channel creation only —
        // guarded here (in addition to the disabled Next button) in case Next is ever
        // reachable another way (e.g. Enter key). Reconnects (existingChannelId set) are
        // exempt, matching the backend's own exemption — see the `else` branch below.
        if (spaceData.description.trim() !== '' && businessRules.trim() === '') {
          setError('Business rules are required.');
          return;
        }
        // See_workflow_tab_button_is_not_shown_in_message_channel — the auto-create effect above
        // now handles the common case (creates the channel in the background and pauses on step 0
        // the moment business rules validate, exactly like email). This manual path is only
        // reached as a fallback if that effect hasn't fired yet (e.g. Next clicked the instant
        // validation resolves) — bare createChannel() (advanceStep defaults true) matches email's
        // own equivalent manual fallback a few lines below in this same function.
        await createChannel();
      } else {
        // Channel already exists — persist business rules if provided, then advance
        if (businessRules.trim()) {
          try {
            setLoading(true);
            setError(null);
            await saveChannelBusinessRules(existingChannelId, businessRules.trim());
          } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to save business rules');
            return;
          } finally {
            setLoading(false);
          }
        }
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
      }
      return;
    }
    
    // Handle voice channel step 0
    if (selectedChannelType === 'voice' && activeStep === 0) {
      if (!voiceChannelNumber.trim()) {
        setError('Please enter a voice channel number');
        return;
      }
      // Create channel for voice
      if (!createdChannelId) {
        await createChannel();
      } else {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
      }
      return;
    }

    // Handle email connection step for email channels
    if (selectedChannelType === 'email' && activeStep === 0) {
      // Business Rules is mandatory once a description is entered, but only while creating a
      // NEW channel (channel_business_rules is not required by the backend on reconnects) —
      // guarded here too in case Next is ever reachable by another path than the disabled
      // button (e.g. Enter key).
      if (!createdChannelId && spaceData.description.trim() !== '' && businessRules.trim() === '') {
        setError('Business rules are required.');
        return;
      }

      if (hasBlockingBusinessRulesValidation()) {
        setError('Validate business rules before continuing.');
        return;
      }

      // Only create channel if it hasn't been created yet
      if (!createdChannelId) {
        await createChannel();
      } else {
        // Channel already exists, update it with connected email as channel name and description
        try {
          setLoading(true);
          setError(null);
          
          // Get the connected email address
          const connectedEmail = selectedEmailProvider === 'other' 
            ? otherEmailCredentials.emailId 
            : oauthData?.email_id;
          
          if (!connectedEmail) {
            throw new Error('No email address found');
          }
          
          if (!createdChannelId) {
            throw new Error('Channel ID not found');
          }
          
          // Call update channel API
          const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
          const response = await fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.CHANNELS.UPDATE(createdChannelId)}`, {
            method: 'PUT',
            headers: {
              'accept': 'application/json',
              'Content-Type': 'application/json',
              ...(token ? { 'Authorization': `Bearer ${token}` } : {})
            },
            body: JSON.stringify({
              name: connectedEmail, // Use connected email as channel name
              description: spaceData.description.trim(), // Use description value
              ...getBusinessRulesSaveExtras(),
              // PR #658 [M1]: spam_auto_push_enabled is persisted immediately when the channel is
              // created (see persistSpamAutoPushSetting calls in createChannel) via its own
              // partial PUT — not sent here too, since a second write on this second-click update
              // would be redundant with the one that already ran at creation time.
            })
          });
          
          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            const serverResult = (errorData?.validation_result ||
              errorData?.detail?.validation_result) as BusinessRulesValidationResult | undefined;
            if (response.status === 422 && serverResult) {
              applyServerValidationResult(serverResult);
              setError('Business rules validation failed. Please review and try again.');
              return;
            }
            throw new Error(errorData?.detail || `HTTP error! status: ${response.status}`);
          }
          
          // Move to next step after successful update
          setActiveStep((prevActiveStep) => prevActiveStep + 1);
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to update channel');
        } finally {
          setLoading(false);
        }
      }
      return;
    }

    // Handle signature & salutation step (only for email)
    if (selectedChannelType === 'email' && activeStep === 1) {
      await createSignatureAndSalutation();
      return;
    }

    // Handle data sources step
    if (
      (selectedChannelType === 'email' && activeStep === 2) ||
      ((selectedChannelType === 'message' || selectedChannelType === 'voice') && activeStep === 1)
    ) {
      // Check if there are any data sources/files to add
      const hasDataSources = spaceData.dataSources && spaceData.dataSources.length > 0;
      
      // If no data sources, just proceed to next step (data sources are optional)
      if (!hasDataSources) {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
        return;
      }
      
      // If there are data sources, attempt to add them
      setDataSourceLoading(true);
      
      try {
        if (dataSourceStepRef.current && dataSourceStepRef.current.handleAddDataSource) {
          const success = await dataSourceStepRef.current.handleAddDataSource();
          if (success) {
            // ${LanguageConstants.datasource_s} added successfully, move to next step
            setActiveStep((prevActiveStep) => prevActiveStep + 1);
          }
          // If not successful, stay on current step (user can try again)
        } else {
          // No ref available, just move to next step
          setActiveStep((prevActiveStep) => prevActiveStep + 1);
        }
      } finally {
        // Always clear loading state
        setDataSourceLoading(false);
      }
      return;
    }

    // Default: move to next step
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const createChannel = async (options?: { advanceStep?: boolean }) => {
    const advanceStep = options?.advanceStep !== false;
    try {
      setLoading(true);
      setError(null);

      // When subscription is disabled, validate via API before allowing channel creation
      if (DISABLE_SUBSCRIPTION) {
        const userData = getLoggedInUserData();
        const userId = userData?.user_id || '';
        const validation = await validateSubscription(userId);
        if (!validation.canCreateChannel) {
          setError(SUBSCRIPTION_LIMIT_MESSAGES.CHANNEL);
          setLoading(false);
          return;
        }
      }

      // Determine which workspace field to use based on selection
      const isNewTag = showNewTagInput && newTagName.trim() !== '';
      
      let createdChannel;

      // If we have OAuth data, use the new email connect API
      if (oauthData) {
        // Determine which workspace field to use based on selection
        const isNewTag = showNewTagInput && newTagName.trim() !== '';
        const emailConnectData = {
          connection_method: "oauth",
          oauth_provider: oauthData.provider == 'office365' ? 'microsoft' : oauthData.provider,
          access_token: oauthData.access_token,
          expires_in: oauthData.expires_in,
          refresh_token: oauthData.refresh_token,
          scope: oauthData.provider == 'office365' ? `${oauthData.scope} offline_access` : oauthData.scope,
          token_type: oauthData.token_type,
          email: oauthData.email_id,
          ...(isNewTag 
            ? { workspace_tag: newTagName.trim() }
            : { workspace_id: spaceData.workspaceId }
          ),
          description: spaceData.description.trim(),
          // List of category values (tags) for the channel
          ...(selectedCategories.length > 0 ? { channel_tags: selectedCategories } : {}),
          // Per-tag subtags dictionary: { "Customer Support": [...], "HR": [...] }
          // W-2: Backend endpoints (channel create + update) must accept Record<string,string[]> for channel_sub_tags.
          ...(Object.keys(selectedKeywordsPerTag).length > 0 ? { channel_sub_tags: selectedKeywordsPerTag } : {}),
          ...getBusinessRulesSaveExtras(),
        };

        const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
        const response = await fetch(API_CONFIG.EMAIL_API_BASE_URL + EMAIL_API_ENDPOINTS.CONNECT, {
          method: 'POST',
          headers: {
            'accept': 'application/json',
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
          },
          body: JSON.stringify(emailConnectData)
        });

        if (!response.ok) {
          // Parse error response to extract the actual error message
          let errorMessage = `HTTP error! status: ${response.status}`;
          try {
            const errorData = await response.json();
            if (errorData.message) {
              errorMessage = errorData.message;
            } else if (errorData.error) {
              errorMessage = errorData.error;
            } else if (typeof errorData.detail === 'string') {
              // FastAPI's plain-string 422 shape (e.g. "channel_business_rules is required
              // when creating a new channel") — previously dropped, falling back to the
              // generic "HTTP error! status: 422" instead of the real message.
              errorMessage = errorData.detail;
            }
          } catch (parseError) {
            // If parsing fails, use the default error message
          }
          throw new Error(errorMessage);
        }

        createdChannel = await response.json();
        setCreatedChannelId(createdChannel.channel_id);
        if (spamAutoPushOnCreate) {
          // PR #658 [M1]: persist as part of this same creation transaction, not deferred to a
          // later "Next" click that may never come. Non-fatal — channel is already created.
          try {
            await persistSpamAutoPushSetting(createdChannel.channel_id, true);
          } catch (spamSettingError) {
            console.error('Error persisting spam auto-push on channel creation:', spamSettingError);
            setError('Channel created, but the spam automation setting could not be saved. You can turn it on later from Channel Settings.');
          }
        }
      } else if (selectedEmailProvider === 'other') {
        // Use IMAP/SMTP connection for other email providers
        const isNewTag = showNewTagInput && newTagName.trim() !== '';
        
        const emailConnectData: ConnectEmailImapRequest = {
          email: otherEmailCredentials.emailId,
          connection_method: "imap",
          app_password: otherEmailCredentials.password.replace(/\s/g, ''), // Remove all spaces from password
          imap_host: otherEmailCredentials.imapServer,
          imap_port: parseInt(otherEmailCredentials.imapPort),
          historical_sync_days: 15,
          archive_to_s3: true,
          archive_format: "xlsx",
          ...(isNewTag 
            ? { workspace_tag: newTagName.trim() }
            : { workspace_id: spaceData.workspaceId }
          ),
          description: spaceData.description.trim(),
          // List of category values (tags) for the channel
          ...(selectedCategories.length > 0 ? { channel_tags: selectedCategories } : {}),
          // Per-tag subtags dictionary: { "Customer Support": [...], "HR": [...] }
          // W-2: Backend endpoints (channel create + update) must accept Record<string,string[]> for channel_sub_tags.
          ...(Object.keys(selectedKeywordsPerTag).length > 0 ? { channel_sub_tags: selectedKeywordsPerTag } : {}),
          ...getBusinessRulesSaveExtras(),
          email_config: {
            imap_settings: {
              server: otherEmailCredentials.imapServer,
              port: parseInt(otherEmailCredentials.imapPort),
              ssl: otherEmailCredentials.imapSsl === 'yes',
              security: otherEmailCredentials.imapSecurity.toUpperCase()
            },
            smtp_settings: {
              server: otherEmailCredentials.smtpServer,
              port: parseInt(otherEmailCredentials.smtpPort),
              auth: otherEmailCredentials.smtpAuth === 'yes',
              ssl: otherEmailCredentials.smtpSsl === 'yes',
              security: otherEmailCredentials.smtpSecurity.toUpperCase()
            }
          }
        };

        const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
        createdChannel = await connectEmailImap(emailConnectData, token);
        setCreatedChannelId(createdChannel.channel_id);
        if (spamAutoPushOnCreate) {
          // PR #658 [M1]: same immediate-persist treatment as the OAuth branch above.
          try {
            await persistSpamAutoPushSetting(createdChannel.channel_id, true);
          } catch (spamSettingError) {
            console.error('Error persisting spam auto-push on channel creation:', spamSettingError);
            setError('Channel created, but the spam automation setting could not be saved. You can turn it on later from Channel Settings.');
          }
        }
      } else if (selectedChannelType === 'message' && (selectedMessagingProvider === 'regular' || selectedMessagingProvider === 'whatsapp' || selectedMessagingProvider === 'slack' || selectedMessagingProvider === 'google_chat' || selectedMessagingProvider === 'teams')) {
        // Handle messaging provider connection (Regular, WhatsApp, Slack, Google Chat, Teams)
        const isNewTag = showNewTagInput && newTagName.trim() !== '';
        // Use persisted tested credentials (modal form fields are cleared after successful test)
        const credentials = (selectedMessagingProvider === 'regular')
          ? messagingProviderCredentials[selectedMessagingProvider]
          : persistedMessagingCredentials[selectedMessagingProvider];
        
        const messagingConnectData: ConnectMessagingRequest = {
          provider: selectedMessagingProvider,
          ...(businessRules.trim() ? { channel_business_rules: businessRules.trim() } : {}),
          ...(spaceData.description.trim() ? { channel_description: spaceData.description.trim() } : {}),
          ...(isNewTag
            ? { workspace_tag: newTagName.trim() }
            : { workspace_id: spaceData.workspaceId }
          ),
        };

        // Add provider-specific fields
        if (selectedMessagingProvider === 'regular') {
          // For regular chat messages
          messagingConnectData.channel_name = channelName.trim() || `Message Channel - ${new Date().toLocaleDateString()}`;
        } else if (selectedMessagingProvider === 'whatsapp') {
          if (!credentials.access_token || !credentials.phone_number_id) {
            throw new Error('WhatsApp credentials are missing. Please test the connection first.');
          }
          messagingConnectData.access_token = credentials.access_token;
          messagingConnectData.phone_number_id = credentials.phone_number_id;
          // Convert sync interval to seconds for API
          const syncInterval = credentials.sync_interval || 30;
          const syncUnit = credentials.sync_interval_unit || 'seconds';
          let syncIntervalInSeconds = syncInterval;
          if (syncUnit === 'minutes') {
            syncIntervalInSeconds = syncInterval * 60;
          } else if (syncUnit === 'hours') {
            syncIntervalInSeconds = syncInterval * 3600;
          }
          // If unit is 'seconds', use the value as-is
          messagingConnectData.sync_interval = syncIntervalInSeconds;
        } else if (selectedMessagingProvider === 'slack') {
          messagingConnectData.bot_token = credentials.bot_token;
          if (credentials.workspace_id) {
            messagingConnectData.workspace_id = credentials.workspace_id;
          }
        } else if (selectedMessagingProvider === 'google_chat') {
          if (!credentials.service_account_key || !credentials.space_id) {
            throw new Error('Google Chat credentials are missing. Please test the connection first.');
          }
          messagingConnectData.service_account_key = credentials.service_account_key;
          messagingConnectData.space_id = credentials.space_id;
        } else if (selectedMessagingProvider === 'teams') {
          messagingConnectData.client_id = credentials.client_id;
          messagingConnectData.client_secret = await encryptSecretForStorage(credentials.client_secret);
          messagingConnectData.tenant_id = credentials.tenant_id;
        }

        const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
        createdChannel = await connectMessaging(messagingConnectData, token);
        setCreatedChannelId(createdChannel.channel_id);
        setMessagingConnectionData(createdChannel);

        // Persist description and business rules via channel update API.
        // The connect endpoint may not save these fields, so we PUT them explicitly.
        await persistChannelDescriptionAndRules({
          channelId: createdChannel.channel_id,
          description: spaceData.description,
          businessRules,
          updateChannel: channelService.updateChannel.bind(channelService),
          showWarning,
        });
      } else if (selectedChannelType === 'message' && selectedMessagingProvider === 'google_chat_inbox') {
        // Google Chat Inbox (DM-only, OAuth2 real-mailbox provider) — deliberately a separate
        // branch, not folded into the combined messaging-provider branch above: this hits a
        // different endpoint with a different request schema (ConnectGoogleChatInboxRequest),
        // not ConnectMessagingRequest. See docs/GoogleChatInbox_DM_FE_Implementation.md §5.7.
        const isNewTag = showNewTagInput && newTagName.trim() !== '';
        // Use persisted OAuth tokens (modal form fields are cleared after the OAuth popup completes)
        const credentials = persistedMessagingCredentials.google_chat_inbox;

        if (!credentials.access_token || !credentials.refresh_token) {
          throw new Error('Google Chat Inbox is not connected yet. Click "Sign in to connect" first.');
        }
        if (!credentials.mailbox_email) {
          throw new Error('Mailbox address is missing. Please reopen the connection dialog and try again.');
        }

        const gchatInboxConnectData: ConnectGoogleChatInboxRequest = {
          mailbox_email: credentials.mailbox_email,
          access_token: credentials.access_token,
          refresh_token: credentials.refresh_token,
          google_client_id: GCHAT_INBOX_CLIENT_ID,
          // NOTE: the backend schema currently requires google_client_secret in this payload.
          // A client secret has no business being bundled into frontend code or sent from the
          // browser — this is a known gap (see docs/GoogleChatInbox_DM_FE_Implementation.md §5.7,
          // §8 item 6) pending a backend fix. Left empty rather than hardcoding a real secret;
          // the connect call will fail with a clear validation error until that's resolved.
          google_client_secret: '',
          ...(isNewTag
            ? { workspace_tag: newTagName.trim() }
            : { workspace_id: spaceData.workspaceId }
          ),
        };

        const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
        createdChannel = await connectGoogleChatInbox(gchatInboxConnectData, token);
        setCreatedChannelId(createdChannel.channel_id);
        setMessagingConnectionData(createdChannel);

        await persistChannelDescriptionAndRules({
          channelId: createdChannel.channel_id,
          description: spaceData.description,
          businessRules,
          updateChannel: channelService.updateChannel.bind(channelService),
          showWarning,
        });
      } else {
        // For voice channels or fallback email channel creation
        let channelDisplayName = '';
        if (selectedChannelType === 'voice') {
          channelDisplayName = `Voice Channel - ${voiceChannelNumber || new Date().toLocaleDateString()}`;
        } else {
          channelDisplayName = `Email Channel - ${new Date().toLocaleDateString()}`;
        }

        const channelData: any = {
          name: channelDisplayName,
          description: spaceData.description.trim(),
          channel_type: selectedChannelType || 'email',
          ...(isNewTag 
            ? { workspace_tag: newTagName.trim() }
            : { workspace_id: spaceData.workspaceId }
          ),
          is_public: true,
          ai_enabled: true,
          channel_settings: {
            additionalProp1: {}
          }
        };

        // Add channel-specific fields
        if (selectedChannelType === 'voice' && voiceChannelNumber.trim()) {
          channelData.voice_channel_number = voiceChannelNumber.trim();
        }

      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
      const response = await fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.CHANNELS.CREATE}`, {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify(channelData)
      });

      if (!response.ok) {
        // Parse error response to extract the actual error message
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorData = await response.json();
          if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.error) {
            errorMessage = errorData.error;
          } else if (typeof errorData.detail === 'string') {
            // FastAPI's plain-string 422 shape — previously dropped here too.
            errorMessage = errorData.detail;
          }
        } catch (parseError) {
          // If parsing fails, use the default error message
        }
        throw new Error(errorMessage);
      }

        createdChannel = await response.json();
      setCreatedChannelId(createdChannel.channel_id);
      }

      // Emit socket event for channel created
      const loggedInUserData = localStorage.getItem(STORAGE_KEYS.LOGGED_IN_USER_DATA);
      if (loggedInUserData) {
        try {
          const userData = JSON.parse(loggedInUserData);
          const eventData = {
            type: SOCKET_KEYS.CHANNEL_CREATED,
            data: {
              emailId: userData.email_id,
              channelId: createdChannel.channel_id
            }
          };
          
          await socketService.sendSocketMessage(eventData);
        } catch (error) {
          // Note: We don't fail the channel creation if socket fails
          // The channel is still created successfully
        }
      }

      // Move to next step, unless this was an auto-create (Odoo #17929) — stay on step 0 so
      // the just-enabled "See Workflow" button is reachable before the user moves on.
      if (advanceStep) {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
      }

      // Don't call onSpaceCreated here - it will be called when the modal is finally closed
      // This prevents the modal from refreshing and resetting during step transitions
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create channel';
      // A plain-string 422 (e.g. "channel_business_rules is required when creating a new
      // channel") surfaces inline near the field, standard form-validation UX, in addition to
      // (not instead of) the generic banner below — covers every create path (OAuth, IMAP,
      // messaging, Google Chat Inbox, fallback) since they all funnel into this one catch.
      if (/business[_ ]rules/i.test(message)) {
        setBusinessRulesServerError(message);
      }
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    // If on first step of step creation, go back to channel type selection
    if (activeStep === 0 && showStepCreation) {
      handleBackToChannelTypeSelection();
    } else {
      setActiveStep((prevActiveStep) => prevActiveStep - 1);
    }
  };

  const handleChangeAccount = () => {
    // Reset all authentication-related state without API call
    setOauthData(null);
    setSelectedEmailProvider(null);
    setConnectionTested(false);
    setGmailLoading(false);
    setOffice365Loading(false);
    
    // Clear popup monitoring intervals
    if (gmailPopupIntervalRef.current) {
      clearInterval(gmailPopupIntervalRef.current);
      gmailPopupIntervalRef.current = null;
    }
    if (office365PopupIntervalRef.current) {
      clearInterval(office365PopupIntervalRef.current);
      office365PopupIntervalRef.current = null;
    }
  };

  const handleDeleteConnection = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get the email address from the connection
      const emailAddress = selectedEmailProvider === 'other' 
        ? otherEmailCredentials.emailId 
        : oauthData?.email_id;
      
      if (!emailAddress) {
        throw new Error('No email address found to delete');
      }
      
      // Get auth token
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
      
      if (!token) {
        throw new Error('Authentication required. Please log in again.');
      }
      
      // Call the delete email data API
      const response = await deleteEmailData(emailAddress, token, false);
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to delete email connection');
      }
      
      // Emit socket event for channel deleted
      const loggedInUserData = localStorage.getItem(STORAGE_KEYS.LOGGED_IN_USER_DATA);
      if (loggedInUserData) {
        try {
          const userData = JSON.parse(loggedInUserData);
          const eventData = {
            type: SOCKET_KEYS.CHANNEL_DELETED,
            data: {
              emailId: userData.email_id,
              channelId: createdChannelId
            }
          };
          
          await socketService.sendSocketMessage(eventData);
        } catch (error) {
          // Handle error silently
        }
      }
      
      // Reset all connection-related data
      setCreatedChannelId('');
      setOauthData(null);
      setSelectedEmailProvider(null);
      setConnectionTested(false);
      setOtherEmailCredentials({
        emailId: '',
        password: '',
        imapServer: '',
        imapPort: '993',
        imapSsl: 'yes',
        imapSecurity: 'ssl',
        smtpServer: '',
        smtpPort: '587',
        smtpAuth: 'yes',
        smtpSsl: 'yes',
        smtpSecurity: 'tls'
      });
      setGmailLoading(false);
      setOffice365Loading(false);
      
      // Reset workspace tag and description
      setSpaceData(prev => ({
        ...prev,
        workspaceTag: '',
        workspaceId: '',
        description: ''
      }));
      setShowNewTagInput(false);
      setNewTagName('');
      // Reset description suggestions state
      setDescriptionSuggestions([]);
      setLoadingDescriptions(false);
      setShowDescriptionField(false);
      setShowDescriptionCards(false);
      // Reset keywords state
      setSelectedKeywordsPerTag({});
      // Reset categories state
      setSelectedCategories([]);
      setBusinessRules('');
      resetBusinessRulesValidation();
      setDebouncedWorkspaceTag('');
      setDebouncedKeywords([]);
      
      // Clear popup monitoring intervals
      if (gmailPopupIntervalRef.current) {
        clearInterval(gmailPopupIntervalRef.current);
        gmailPopupIntervalRef.current = null;
      }
      if (office365PopupIntervalRef.current) {
        clearInterval(office365PopupIntervalRef.current);
        office365PopupIntervalRef.current = null;
      }
    } catch (err) {
      console.error('Error deleting connection:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete email connection');
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    // Skip current step and move to next
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  // Helper function to determine if skip button should be shown
  const shouldShowSkipButton = () => {
    // Don't show skip on first step (step 0) or last step
    if (activeStep === 0 || activeStep === steps.length - 1) {
      return false;
    }

    // For email channels: show skip on step 1 (Signature & Salutation) and step 2 (Data Sources)
    if (selectedChannelType === 'email') {
      return activeStep === 1 || activeStep === 2;
    }

    // For message channels: show skip on step 1 (Data Sources)
    if (selectedChannelType === 'message') {
      return activeStep === 1;
    }

    return false;
  };

  // True when current step is Data Sources (email step 2, message/voice step 1) for loading/disabled state
  const isOnDataSourceStep =
    (selectedChannelType === 'email' && activeStep === 2) ||
    ((selectedChannelType === 'message' || selectedChannelType === 'voice') && activeStep === 1);

  const handleChannelTypeSelect = (channelType: 'email' | 'message' | 'voice') => {
    setSelectedChannelType(channelType);
    setShowChannelTypeSelection(false);
    setShowStepCreation(true);
    setActiveStep(0);
    // Clear any error messages when selecting a channel type
    setError(null);
    // Reset messaging provider when selecting message channel type
    if (channelType === 'message') {
      setSelectedMessagingProvider(null);
      setSelectedProviderForModal(null);
      setMessagingConnectionTested(false);
      setMessagingTestSuccessData(null);
      setPersistedMessagingCredentials({ ...EMPTY_MESSAGING_CREDENTIALS });
      setMessagingConnectionData(null);
    }
  };

  const handleBackToChannelTypeSelection = () => {
    setShowChannelTypeSelection(true);
    setShowStepCreation(false);
    setSelectedChannelType(null);
    setActiveStep(0);
    setChannelName('');
    setVoiceChannelNumber('');
    // Clear any error messages when going back
    setError(null);
    // Reset messaging provider state
    setSelectedMessagingProvider(null);
    setSelectedProviderForModal(null);
    setMessagingConnectionTested(false);
    setMessagingTestSuccessData(null);
    setPersistedMessagingCredentials({ ...EMPTY_MESSAGING_CREDENTIALS });
    setMessagingConnectionData(null);
  };

  const handleClose = () => {
    // If we have a created channel ID, call the callback to refresh the channels list
    if (createdChannelId && onSpaceCreated) {
      onSpaceCreated();
    }
    
    // Reset channel type selection state
    setSelectedChannelType(null);
    setShowChannelTypeSelection(true);
    setShowStepCreation(false);
    setChannelName('');
    setVoiceChannelNumber('');
    
    setActiveStep(0);
    setSpaceData({
      name: '',
      description: '',
      workspaceTag: '',
      workspaceId: '',
      selectedApps: [],
      dataSources: [],
      signatureAndSalutation: [],
    });
    setShowSignatureModal(false);
    setCreatedChannelId('');
    setShowNewTagInput(false);
    setNewTagName('');
    setSelectedEmailProvider(null);
    // Reset keywords and categories state
    setSelectedKeywordsPerTag({});
    setSelectedCategories([]);
    setTagRows([{ id: crypto.randomUUID(), tag: '' }]);
    setDebouncedWorkspaceTag('');
    setDebouncedKeywords([]);
    setOtherEmailCredentials({
      emailId: '',
      password: '',
      imapServer: '',
      imapPort: '993',
      imapSsl: 'yes',
      imapSecurity: 'ssl',
      smtpServer: '',
      smtpPort: '587',
      smtpAuth: 'yes',
      smtpSsl: 'yes',
      smtpSecurity: 'tls'
    });
    setConnectionTested(false);
    setTestingConnection(false);
    setGmailLoading(false);
    setOffice365Loading(false);
    setOauthData(null);
    setSelectedEmailProvider(null);
    // Reset description suggestions state
    setDescriptionSuggestions([]);
    setLoadingDescriptions(false);
    setShowDescriptionField(false);
    setShowDescriptionCards(false);
    setBusinessRules('');
    resetBusinessRulesValidation();
    // Reset messaging provider state
    setSelectedMessagingProvider(null);
    setPersistedMessagingCredentials({ ...EMPTY_MESSAGING_CREDENTIALS });
    setWhatsappLoading(false);
    setMessagingConnectionData(null);
    
    // Clear error state when closing modal
    setError(null);
    
    // Clear popup monitoring intervals
    if (gmailPopupIntervalRef.current) {
      clearInterval(gmailPopupIntervalRef.current);
      gmailPopupIntervalRef.current = null;
    }
    if (office365PopupIntervalRef.current) {
      clearInterval(office365PopupIntervalRef.current);
      office365PopupIntervalRef.current = null;
    }
    
    onClose();
  };

  const handleModalClose = (event: any, reason: string) => {
    if (reason === 'backdropClick') {
      return; // Prevent closing when clicking outside
    }
    handleClose();
  };

  const handleSpaceDataChange = (field: keyof SpaceData, value: any) => {
    setSpaceData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  /**
   * Opens the Google Chat Inbox OAuth popup. Modeled closely on handleGmailOAuth below, with two
   * deliberate differences:
   *  1. Completion is handled entirely by the handleOAuthData listener's dedicated
   *     google_chat_inbox branch (which marks the connection "tested" directly), not by a
   *     POST /messaging/test-connection round trip — that endpoint doesn't support this
   *     provider, and there's nothing to "test" separately from the OAuth handshake itself.
   *  2. redirect_uri points at the EXISTING /auth/google/callback route (Gmail's), not the
   *     dedicated GoogleChatInboxOAuthCallback.tsx page — TEMPORARILY, because the dedicated
   *     redirect_uri isn't yet registered in Google Cloud Console for this OAuth client
   *     (redirect_uri_mismatch), while Gmail's route already is. Tagged via `state` so the
   *     shared callback page knows which flow it's completing. Revert to the dedicated route
   *     (already built, just unused right now) once Console registration lands.
   * See docs/GoogleChatInbox_DM_FE_Implementation.md §5.4/§5.5/§5.6.
   */
  const handleGoogleChatInboxOAuth = () => {
    setError(null);
    setGchatInboxOauthLoading(true);
    const currentOrigin = window.location.origin;
    const redirectUri = `${currentOrigin}/auth/google/callback`;
    const state = 'google_chat_inbox_oauth';
    // Scopes per TDD-002 §5.1 — unverified against a live OAuth client (SPIKE-A); confirm these
    // are accepted before relying on this flow in a real environment.
    const scope =
      'https://www.googleapis.com/auth/chat.spaces.readonly ' +
      'https://www.googleapis.com/auth/chat.messages.readonly ' +
      'https://www.googleapis.com/auth/chat.messages ' +
      'https://www.googleapis.com/auth/contacts.readonly ' +
      'https://www.googleapis.com/auth/userinfo.email openid';
    const responseType = 'code';
    const accessType = 'offline';
    const prompt = 'select_account consent';

    const googleAuthUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
      `client_id=${GCHAT_INBOX_CLIENT_ID}&` +
      `redirect_uri=${encodeURIComponent(redirectUri)}&` +
      `scope=${encodeURIComponent(scope)}&` +
      `response_type=${responseType}&` +
      `access_type=${accessType}&` +
      `state=${encodeURIComponent(state)}&` +
      `prompt=${encodeURIComponent(prompt)}`;

    const popup = window.open(googleAuthUrl, '_blank', 'width=600,height=600,scrollbars=yes,resizable=yes');

    if (gchatInboxPopupIntervalRef.current) {
      clearInterval(gchatInboxPopupIntervalRef.current);
    }

    gchatInboxPopupIntervalRef.current = setInterval(() => {
      if (popup?.closed) {
        clearInterval(gchatInboxPopupIntervalRef.current!);
        gchatInboxPopupIntervalRef.current = null;
        setGchatInboxOauthLoading(false);
      }
    }, 1000);
  };

  const handleGmailOAuth = () => {
    setSelectedEmailProvider('gmail');
    setError(null); // Clear any previous errors when selecting Gmail
    oauthCancelledToastShownRef.current = false; // Allow one cancellation toast per OAuth attempt
    setGmailLoading(true);
    // Dynamically determine redirectUri based on current domain
    const currentOrigin = window.location.origin;
    const redirectUri = `${currentOrigin}/auth/google/callback`;
    // Gmail API: readonly + send + modify (labels, trash, etc.); OIDC + profile for account identity
    const scope =
      'https://www.googleapis.com/auth/gmail.readonly https://www.googleapis.com/auth/gmail.send https://www.googleapis.com/auth/gmail.modify https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile openid';
    const responseType = 'code';
    const accessType = 'offline';
    const prompt = 'select_account consent';
    
    const googleAuthUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
      `client_id=${GMAIL_CLIENT_ID}&` +
      `redirect_uri=${encodeURIComponent(redirectUri)}&` +
      `scope=${encodeURIComponent(scope)}&` +
      `response_type=${responseType}&` +
      `access_type=${accessType}&` +
      `prompt=${encodeURIComponent(prompt)}`;
    
    // Open popup and monitor for closure
    const popup = window.open(googleAuthUrl, '_blank', 'width=600,height=600,scrollbars=yes,resizable=yes');
    
    // Clear any existing interval
    if (gmailPopupIntervalRef.current) {
      clearInterval(gmailPopupIntervalRef.current);
    }
    
    // Monitor popup closure
    gmailPopupIntervalRef.current = setInterval(() => {
      if (popup?.closed) {
        clearInterval(gmailPopupIntervalRef.current!);
        gmailPopupIntervalRef.current = null;
        setGmailLoading(false);
      }
    }, 1000);
  };

  /** Open OAuth popup for Office 365 (called from info modal or directly). */
  const handleOffice365OAuth = () => {
    setSelectedEmailProvider('office365');
    setError(null); // Clear any previous errors when selecting Office365
    setOffice365Loading(true);
    // Dynamically determine redirectUri based on current domain
    const currentOrigin = window.location.origin;
    const redirectUri = `${currentOrigin}/auth/office365/callback`;
    const scope = 'https://graph.microsoft.com/Mail.Read https://graph.microsoft.com/Mail.ReadWrite https://graph.microsoft.com/Mail.Send https://graph.microsoft.com/User.Read openid offline_access';
    const responseType = 'code';
    const responseMode = 'query';
    const state = 'office365_oauth_state';
    
    const office365AuthUrl = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?` +
      `client_id=${OFFICE365_CLIENT_ID}&` +
      `response_type=${responseType}&` +
      `redirect_uri=${encodeURIComponent(redirectUri)}&` +
      `scope=${encodeURIComponent(scope)}&` +
      `response_mode=${responseMode}&` +
      `state=${state}&` +
      `prompt=select_account`;
    
    // Open popup and monitor for closure
    const popup = window.open(office365AuthUrl, '_blank', 'width=600,height=600,scrollbars=yes,resizable=yes');
    
    // Clear any existing interval
    if (office365PopupIntervalRef.current) {
      clearInterval(office365PopupIntervalRef.current);
    }
    
    // Monitor popup closure
    office365PopupIntervalRef.current = setInterval(() => {
      if (popup?.closed) {
        clearInterval(office365PopupIntervalRef.current!);
        office365PopupIntervalRef.current = null;
        setOffice365Loading(false);
      }
    }, 1000);
  };

  /** Show Office 365 info modal on card click; user can get admin link then "Connect Outlook" to start OAuth. */
  const handleOffice365CardClick = () => {
    setSelectedEmailProvider('office365');
    setError(null);
    setAdminConsentDialogOpen(true);
  };

  const handleTestConnection = async () => {
    setTestingConnection(true);
    setError(null);
    
    try {
      const payload = {
        email: otherEmailCredentials.emailId,
        connection_method: "imap",
        app_password: otherEmailCredentials.password.replace(/\s/g, ''), // Remove all spaces from password
        imap_host: otherEmailCredentials.imapServer,
        imap_port: parseInt(otherEmailCredentials.imapPort)
      };

      const response = await fetch(`${API_CONFIG.EMAIL_API_BASE_URL}${EMAIL_API_ENDPOINTS.TEST_CONNECTION}`, {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (!response.ok) {
        // Extract user-friendly error message
        let errorMessage = 'Connection test failed. Please check your credentials and try again.';
        
        if (data.message) {
          // Clean up the error message for better readability
          if (data.message.includes('Invalid credentials') || data.message.includes('AUTHENTICATIONFAILED')) {
            errorMessage = 'Authentication failed. Please check your email and password are correct.';
          } else if (data.message.includes('connection')) {
            errorMessage = data.message;
          } else {
            errorMessage = data.message;
          }
        } else if (data.detail) {
          errorMessage = data.detail;
        }
        
        throw new Error(errorMessage);
      }
      
      if (data.success) {
        setConnectionTested(true);
        setError(null); // Clear any previous errors
      } else {
        throw new Error(data.message || 'Connection test failed');
      }
    } catch (error) {
      console.error('Connection test failed:', error);
      setConnectionTested(false);
      setError(error instanceof Error ? error.message : 'Failed to test connection. Please check your credentials and try again.');
    } finally {
      setTestingConnection(false);
    }
  };

  const handleOpenMessagingProviderModal = (provider: 'whatsapp' | 'slack' | 'google_chat' | 'google_chat_inbox' | 'teams') => {
    // Blur active element and the Workspace field so neither stays focused when the provider modal opens
    (document.activeElement as HTMLElement)?.blur();
    messagingWorkspaceInputRef.current?.blur();
    // Blur again next frame in case focus moved to the Workspace field after the button blur
    requestAnimationFrame(() => {
      messagingWorkspaceInputRef.current?.blur();
    });
    setSelectedProviderForModal(provider);
    setMessagingProviderModalOpen(true);
    setMessagingConnectionTested(false);
    setMessagingTestSuccessData(null);
    setGoogleChatPickerOpen(false); // this path always resolves a specific provider directly
    setError(null); // Clear any previous errors when opening modal
    // Clear regular selection when opening provider modal
    if (selectedMessagingProvider === 'regular') {
      setSelectedMessagingProvider(null);
    }
  };

  /**
   * Opens the merged "Google Chat" dialog in picker mode — Space vs. Direct Messages — instead of
   * resolving directly to a provider. selectedProviderForModal stays null until the user picks
   * one (see handleSelectGoogleChatMode), at which point everything downstream (dialog title,
   * fields, footer button, createChannel wiring) works exactly as it already did for a directly
   * opened provider — only the entry point changed, not the underlying two-provider model.
   */
  const handleOpenGoogleChatPicker = () => {
    (document.activeElement as HTMLElement)?.blur();
    messagingWorkspaceInputRef.current?.blur();
    requestAnimationFrame(() => {
      messagingWorkspaceInputRef.current?.blur();
    });
    setSelectedProviderForModal(null);
    setGoogleChatPickerOpen(true);
    setMessagingProviderModalOpen(true);
    setMessagingConnectionTested(false);
    setMessagingTestSuccessData(null);
    setError(null);
    if (selectedMessagingProvider === 'regular') {
      setSelectedMessagingProvider(null);
    }
  };

  const handleSelectGoogleChatMode = (mode: 'google_chat' | 'google_chat_inbox') => {
    setGoogleChatPickerOpen(false);
    setSelectedProviderForModal(mode);
  };

  const resetMessagingProviderCredentials = (provider: 'whatsapp' | 'slack' | 'google_chat' | 'google_chat_inbox' | 'teams' | null) => {
    if (!provider) return;

    const initialCredentials: any = {
      whatsapp: { access_token: '', phone_number_id: '', sync_interval: 30, sync_interval_unit: 'seconds' },
      slack: { bot_token: '', workspace_id: '' },
      google_chat: { service_account_key: '', space_id: '' },
      google_chat_inbox: { mailbox_email: '', access_token: '', refresh_token: '' },
      teams: { client_id: '', client_secret: '', tenant_id: '' }
    };

    setMessagingProviderCredentials({
      ...messagingProviderCredentials,
      [provider]: initialCredentials[provider]
    });
  };

  const handleCloseMessagingProviderModal = (event?: {}, reason?: string) => {
    // Prevent closing when clicking outside the modal (backdrop click)
    if (reason === 'backdropClick') {
      return;
    }

    // If a Google Chat Inbox OAuth popup is still open, closing this dialog shouldn't leave a
    // dangling popup-monitor interval or a stuck loading state behind.
    if (gchatInboxPopupIntervalRef.current) {
      clearInterval(gchatInboxPopupIntervalRef.current);
      gchatInboxPopupIntervalRef.current = null;
    }
    setGchatInboxOauthLoading(false);

    // Reset credentials for the provider that was being configured
    const providerToReset = selectedProviderForModal;
    resetMessagingProviderCredentials(providerToReset);

    setMessagingProviderModalOpen(false);
    setSelectedProviderForModal(null);
    setGoogleChatPickerOpen(false);
    setMessagingConnectionTested(false);
    setMessagingTestSuccessData(null);
    setGchatKeyFileName('');
    setError(null);
  };

  const handleTestMessagingConnection = async () => {
    if (!selectedProviderForModal) return;
    
    setTestingMessagingConnection(true);
    setError(null);
    
    try {
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';

      const credentials = messagingProviderCredentials[selectedProviderForModal];
      let payload: any = {
        provider: selectedProviderForModal
      };

      // Add provider-specific fields
      if (selectedProviderForModal === 'whatsapp') {
        if (!credentials.access_token || !credentials.phone_number_id) {
          throw new Error('Please fill in all required fields');
        }
        payload.access_token = credentials.access_token;
        payload.phone_number_id = credentials.phone_number_id;
        // Convert sync interval to seconds for API
        const syncInterval = credentials.sync_interval || 30;
        const syncUnit = credentials.sync_interval_unit || 'seconds';
        let syncIntervalInSeconds = syncInterval;
        if (syncUnit === 'minutes') {
          syncIntervalInSeconds = syncInterval * 60;
        } else if (syncUnit === 'hours') {
          syncIntervalInSeconds = syncInterval * 3600;
        }
        // If unit is 'seconds', use the value as-is
        payload.sync_interval = syncIntervalInSeconds;
      } else if (selectedProviderForModal === 'slack') {
        if (!credentials.bot_token) {
          throw new Error('Please fill in all required fields');
        }
        payload.bot_token = credentials.bot_token;
        if (credentials.workspace_id) {
          payload.workspace_id = credentials.workspace_id;
        }
      } else if (selectedProviderForModal === 'google_chat') {
        if (!credentials.service_account_key || !credentials.space_id) {
          throw new Error('Please fill in all required fields');
        }
        payload.service_account_key = credentials.service_account_key;
        payload.space_id = credentials.space_id;
      } else if (selectedProviderForModal === 'teams') {
        if (!credentials.client_id || !credentials.client_secret || !credentials.tenant_id) {
          throw new Error('Please fill in all required fields');
        }
        payload.client_id = credentials.client_id;
        payload.client_secret = credentials.client_secret;
        payload.tenant_id = credentials.tenant_id;
      }

      const response = await fetch(`${API_CONFIG.EMAIL_API_BASE_URL}${MESSAGING_API_ENDPOINTS.TEST_CONNECTION}`, {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (!response.ok) {
        let errorMessage = 'Connection test failed. Please check your credentials and try again.';
        if (data.message) {
          errorMessage = data.message;
        } else if (data.detail) {
          errorMessage = typeof data.detail === 'string' ? data.detail : data.detail[0]?.msg || errorMessage;
        }
        throw new Error(errorMessage);
      }
      
      if (data.success) {
        setMessagingConnectionTested(true);
        setMessagingTestSuccessData(data);
        setError(null);
        // Automatically set the selected provider after successful test
        // This will show the success message in the main content (configure channel screen)
        if (selectedProviderForModal) {
          setSelectedMessagingProvider(selectedProviderForModal);
          // Snapshot live credentials into persisted map before the form-field reset wipes them
          setPersistedMessagingCredentials(prev => ({
            ...prev,
            [selectedProviderForModal]: messagingProviderCredentials[selectedProviderForModal],
          }));
        }
        // Reset credentials before closing the modal
        const providerToReset = selectedProviderForModal;
        resetMessagingProviderCredentials(providerToReset);
        
        // Close the modal after successful test (but preserve state for main content)
        setMessagingProviderModalOpen(false);
        setSelectedProviderForModal(null);
      } else {
        throw new Error(data.message || 'Connection test failed');
      }
    } catch (error) {
      console.error('Messaging connection test failed:', error);
      setMessagingConnectionTested(false);
      setMessagingTestSuccessData(null);
      const errorMessage = error instanceof Error ? error.message : 'Failed to test connection. Please check your credentials and try again.';
      setError(errorMessage);
      
      // Reset credentials before closing the modal
      const providerToReset = selectedProviderForModal;
      resetMessagingProviderCredentials(providerToReset);
      
      // Close the modal after error - error will show in main content
      setMessagingProviderModalOpen(false);
      setSelectedProviderForModal(null);
    } finally {
      setTestingMessagingConnection(false);
    }
  };

  const handleRemoveMessagingAccount = () => {
    setMessagingConnectionTested(false);
    setMessagingTestSuccessData(null);
    setPersistedMessagingCredentials({ ...EMPTY_MESSAGING_CREDENTIALS });
    setSelectedMessagingProvider(null);
    setError(null); // Clear any error messages when removing messaging account
    // Reset credentials for the selected provider
    if (selectedProviderForModal) {
      setMessagingProviderCredentials({
        ...messagingProviderCredentials,
        [selectedProviderForModal]: {
          whatsapp: { access_token: '', phone_number_id: '', sync_interval: 30 },
          slack: { bot_token: '', workspace_id: '' },
          google_chat: { service_account_key: '', space_id: '' },
          google_chat_inbox: { mailbox_email: '', access_token: '', refresh_token: '' },
          teams: { client_id: '', client_secret: '', tenant_id: '' }
        }[selectedProviderForModal]
      });
    }
  };

  const handleMessagingProviderCredentialsChange = (field: string, value: string | number) => {
    if (!selectedProviderForModal) return;
    setMessagingProviderCredentials({
      ...messagingProviderCredentials,
      [selectedProviderForModal]: {
        ...messagingProviderCredentials[selectedProviderForModal],
        [field]: value
      }
    });
  };

  const handleGchatKeyFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.json') && file.type !== 'application/json') {
      setError('Please upload a valid JSON file (.json).');
      event.target.value = '';
      return;
    }
    if (file.size > 100_000) {
      setError('File is too large. Service account key files are typically a few KB — please check you selected the correct file.');
      event.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        JSON.parse(content);
        handleMessagingProviderCredentialsChange('service_account_key', content);
        setGchatKeyFileName(file.name);
        setError(null);
      } catch {
        setError('The uploaded file is not valid JSON. Please check the file and try again.');
      }
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  const handleGchatKeyClear = () => {
    handleMessagingProviderCredentialsChange('service_account_key', '');
    setGchatKeyFileName('');
  };

  const handleOtherEmailCredentialsChange = (field: string, value: string) => {
    setOtherEmailCredentials(prev => ({
      ...prev,
      [field]: value
    }));
    // Reset connection test when credentials change
    setConnectionTested(false);
  };

  const handleCreateSpace = () => {
    // Here you would typically make an API call to create the space
    // The handleClose will call onSpaceCreated if we have a createdChannelId
    handleClose();
  };

  // Helper function to check if rich text content is actually empty
  const isRichTextEmpty = (content: string): boolean => {
    if (!content || content.trim() === '') return true;
    // Remove HTML tags and check if there's actual text content
    const textContent = content.replace(/<[^>]*>/g, '').trim();
    return textContent === '';
  };

  const isStepValid = (step: number) => {
    // For message channels, validate step 0 based on provider
    if (selectedChannelType === 'message' && step === 0) {
      // channel_business_rules is only required by the backend when creating a brand-new
      // channel — not when reconnecting to one that already has a channel_id.
      const isNewMessageChannel = !createdChannelId && !messagingConnectionData?.channel_id;
      // Check if workspace and description are filled
      const hasRequiredFields = spaceData.description.trim() !== '' &&
             (spaceData.workspaceTag.trim() !== '' || (showNewTagInput && newTagName.trim() !== '')) &&
             (!isNewMessageChannel || businessRules.trim() !== '');

      // Check if provider is selected
      if (!selectedMessagingProvider) {
        return false;
      }
      
      // If WhatsApp/Slack/Google Chat/Google Chat Inbox/Teams provider, validate that connection was tested
      if (selectedMessagingProvider === 'whatsapp' || selectedMessagingProvider === 'slack' ||
          selectedMessagingProvider === 'google_chat' || selectedMessagingProvider === 'google_chat_inbox' ||
          selectedMessagingProvider === 'teams') {
        // If already connected, step is valid if required fields are filled
        if (messagingConnectionData?.channel_id) {
          return hasRequiredFields;
        }
        // Connection must be tested successfully
        return messagingConnectionTested && hasRequiredFields;
      }
      
      // If regular provider, validate channel name and required fields
      if (selectedMessagingProvider === 'regular') {
        return channelName.trim().length > 0 && hasRequiredFields;
      }
      
      return false;
    }
    
    // For voice channels, validate voice number, workspace tag, and description on step 0
    if (selectedChannelType === 'voice' && step === 0) {
      const hasRequiredFields = spaceData.description.trim() !== '' && 
             (spaceData.workspaceTag.trim() !== '' || (showNewTagInput && newTagName.trim() !== ''));
      return voiceChannelNumber.trim().length > 0 && hasRequiredFields;
    }

    // For email channels, validate email connection on step 0
    if (selectedChannelType === 'email' && step === 0) {
      // Check if workspace, description, tags, and sub-tags are filled
      const hasRequiredFields = spaceData.description.trim() !== '' &&
             spaceData.workspaceTag.trim() !== '' &&
             selectedCategories.length > 0 &&
             flatSelectedKeywords.length > 0;
      
      // Check if any email connection is established (or channel already created)
      const hasEmailConnection = createdChannelId !== '' || 
                               oauthData !== null || 
                               (selectedEmailProvider === 'other' && connectionTested);
      
      return hasRequiredFields && hasEmailConnection;
    }

    // For email channels, step 1 is Signature & Salutation
    if (selectedChannelType === 'email' && step === 1) {
      // Step 1: Signature & Salutation - require at least one of salutation or signature (name is optional)
      if (spaceData.signatureAndSalutation.length === 0) return true; // Allow skipping if empty
      
      const sigData = spaceData.signatureAndSalutation[0];
      const hasSalutation = sigData?.salutation?.trim() !== '';
      const hasSignature = sigData?.signature && !isRichTextEmpty(sigData.signature);
      
      return hasSalutation || hasSignature; // At least one is required, but name is optional
    }

    // Data sources step (step 2 for email, step 1 for message/voice)
    if (
      (selectedChannelType === 'email' && step === 2) ||
      ((selectedChannelType === 'message' || selectedChannelType === 'voice') && step === 1)
    ) {
      return true; // Data sources are optional
    }

    // Apps selection step (step 3 for email, step 2 for message/voice)
    if (
      (selectedChannelType === 'email' && step === 3) ||
      ((selectedChannelType === 'message' || selectedChannelType === 'voice') && step === 2)
    ) {
      return true; // Apps selection is optional
    }

    // Skills step (step 4 for email, step 3 for message)
    if (
      (selectedChannelType === 'email' && step === 4) ||
      (selectedChannelType === 'message' && step === 3)
    ) {
      return true; // Skills are optional during setup
    }

    // Automation step (email only - step 5)
    if (selectedChannelType === 'email' && step === 5) {
      return true; // Automation is optional during setup
    }

    return true;
  };

  const renderConnectedMailId = () => {
    return `: ${selectedEmailProvider === 'other' ? otherEmailCredentials.emailId : oauthData?.email_id}`;
  };

  const renderStepContent = (step: number) => {
    // For message channels, step 0 shows provider selection + workspace + description + channel name/connection
    if (selectedChannelType === 'message' && step === 0) {
      const isChannelCreated = !!createdChannelId || !!messagingConnectionData?.channel_id;
      const isGoogleChatActive =
        selectedMessagingProvider === 'google_chat' ||
        (messagingConnectionTested && selectedProviderForModal === 'google_chat');
      const isGoogleChatInboxActive =
        selectedMessagingProvider === 'google_chat_inbox' ||
        (messagingConnectionTested && selectedProviderForModal === 'google_chat_inbox');
      // Drives the single merged "Google Chat" tile's highlighting — active if EITHER underlying
      // mode (Space or DMs) is the connected/tested one.
      const isGoogleChatCombinedActive = isGoogleChatActive || isGoogleChatInboxActive;
      const isSlackActive =
        selectedMessagingProvider === 'slack' ||
        (messagingConnectionTested && selectedProviderForModal === 'slack');
      const isTeamsActive =
        selectedMessagingProvider === 'teams' ||
        (messagingConnectionTested && selectedProviderForModal === 'teams');
      
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }} data-testid="add-space-modal-messaging-provider-step">
          {/* Provider Selection */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Tooltip title="" arrow placement="top">
              <span style={{ width: '100%' }}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => {
                    setSelectedMessagingProvider('regular');
                    // Clear any messaging connection state when switching to regular
                    setMessagingConnectionTested(false);
                    setMessagingTestSuccessData(null);
                    setSelectedProviderForModal(null);
                    // Clear any error messages when switching providers
                    setError(null);
                  }}
                  disabled={isChannelCreated}
                  sx={(theme) => ({
                    py: 2,
                    borderColor: selectedMessagingProvider === 'regular' ? theme.palette.success.main : theme.palette.primary.main,
                    color: selectedMessagingProvider === 'regular' ? theme.palette.success.main : theme.palette.primary.main,
                    backgroundColor: selectedMessagingProvider === 'regular' ? theme.palette.success.light + '20' : 'transparent',
                    '&:hover': {
                      borderColor: selectedMessagingProvider === 'regular' ? theme.palette.success.main : theme.palette.primary.dark,
                      backgroundColor: selectedMessagingProvider === 'regular' ? theme.palette.success.light + '20' : theme.palette.primary.light + '20'
                    }
                  })}
                  startIcon={
                    <ChatIcon sx={{ fontSize: 24 }} />
                  }
                >
                  {selectedMessagingProvider === 'regular' ? '✓ Regular' : 'Regular'}
                </Button>
              </span>
            </Tooltip>
            {!hiddenMessagingProviders.has('whatsapp') && <Tooltip title="" arrow placement="top">
              <span style={{ width: '100%' }}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => handleOpenMessagingProviderModal('whatsapp')}
                  disabled={isChannelCreated}
                  sx={(theme) => ({
                    py: 2,
                    whiteSpace: 'nowrap',
                    borderColor: (selectedMessagingProvider === 'whatsapp' || messagingConnectionTested && selectedProviderForModal === 'whatsapp') ? theme.palette.success.main : theme.palette.primary.main,
                    color: (selectedMessagingProvider === 'whatsapp' || messagingConnectionTested && selectedProviderForModal === 'whatsapp') ? theme.palette.success.main : theme.palette.primary.main,
                    backgroundColor: (selectedMessagingProvider === 'whatsapp' || messagingConnectionTested && selectedProviderForModal === 'whatsapp') ? theme.palette.success.light + '20' : 'transparent',
                    '&:hover': {
                      borderColor: (selectedMessagingProvider === 'whatsapp' || messagingConnectionTested && selectedProviderForModal === 'whatsapp') ? theme.palette.success.main : theme.palette.primary.dark,
                      backgroundColor: (selectedMessagingProvider === 'whatsapp' || messagingConnectionTested && selectedProviderForModal === 'whatsapp') ? theme.palette.success.light + '20' : theme.palette.primary.light + '20'
                    }
                  })}
                  startIcon={
                    <Box
                      component="img"
                      src="/images/apps/whatsapp.png"
                      alt="WhatsApp"
                      sx={{ 
                        width: 24, 
                        height: 24,
                        minWidth: 24,
                        minHeight: 24,
                        maxWidth: 24,
                        maxHeight: 24,
                        objectFit: 'contain',
                        display: 'block'
                      }}
                    />
                  }
                >
                  {(selectedMessagingProvider === 'whatsapp' || (messagingConnectionTested && selectedProviderForModal === 'whatsapp')) ? '✓ WhatsApp' : 'WhatsApp'}
                </Button>
              </span>
            </Tooltip>}
            {!hiddenMessagingProviders.has('slack') && <span style={{ width: '100%' }}>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => handleOpenMessagingProviderModal('slack')}
                disabled={isChannelCreated}
                sx={(theme) => ({
                  py: 2,
                  borderColor: isSlackActive ? theme.palette.success.main : theme.palette.primary.main,
                  color: isSlackActive ? theme.palette.success.main : theme.palette.primary.main,
                  backgroundColor: isSlackActive ? alpha(theme.palette.success.light, 0.12) : 'transparent',
                  '&:hover': {
                    borderColor: isSlackActive ? theme.palette.success.main : theme.palette.primary.dark,
                    backgroundColor: isSlackActive
                      ? alpha(theme.palette.success.light, 0.12)
                      : alpha(theme.palette.primary.light, 0.12),
                  },
                })}
                startIcon={
                  <Box
                    component="img"
                    src="/images/apps/slack.png"
                    alt="Slack"
                    sx={{
                      width: 24,
                      height: 24,
                      minWidth: 24,
                      minHeight: 24,
                      maxWidth: 24,
                      maxHeight: 24,
                      objectFit: 'contain',
                      display: 'block'
                    }}
                  />
                }
              >
                {isSlackActive ? '✓ Slack' : 'Slack'}
              </Button>
            </span>}
            {/* Single Google Chat entry point — opens a Space-vs-Direct-Messages picker inside the
                dialog (see googleChatPickerOpen) rather than two separate top-level tiles. The two
                modes are still entirely separate providers/schemas under the hood
                (selectedProviderForModal resolves to 'google_chat' or 'google_chat_inbox' exactly
                as before) — only the entry point is merged. */}
            {(!hiddenMessagingProviders.has('google_chat') || !hiddenMessagingProviders.has('google_chat_inbox')) && <span style={{ width: '100%' }}>
              <Button
                variant="outlined"
                fullWidth
                onClick={handleOpenGoogleChatPicker}
                disabled={isChannelCreated}
                sx={(theme) => ({
                  py: 2,
                  whiteSpace: 'nowrap',
                  borderColor: isGoogleChatCombinedActive ? theme.palette.success.main : theme.palette.primary.main,
                  color: isGoogleChatCombinedActive ? theme.palette.success.main : theme.palette.primary.main,
                  backgroundColor: isGoogleChatCombinedActive ? alpha(theme.palette.success.light, 0.12) : 'transparent',
                  '&:hover': {
                    borderColor: isGoogleChatCombinedActive ? theme.palette.success.main : theme.palette.primary.dark,
                    backgroundColor: isGoogleChatCombinedActive
                      ? alpha(theme.palette.success.light, 0.12)
                      : alpha(theme.palette.primary.light, 0.12)
                  }
                })}
                startIcon={
                  <Box
                    component="img"
                    src="/images/apps/gchat.png"
                    alt="Google Chat"
                    sx={{
                      width: 24,
                      height: 24,
                      minWidth: 24,
                      minHeight: 24,
                      maxWidth: 24,
                      maxHeight: 24,
                      objectFit: 'contain',
                      display: 'block'
                    }}
                  />
                }
              >
                {isGoogleChatCombinedActive ? '✓ Google Chat' : 'Google Chat'}
              </Button>
            </span>}
            {!hiddenMessagingProviders.has('teams') && <span style={{ width: '100%' }}>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => handleOpenMessagingProviderModal('teams')}
                disabled={isChannelCreated}
                sx={(theme) => ({
                  py: 2,
                  whiteSpace: 'nowrap',
                  borderColor: isTeamsActive ? theme.palette.success.main : theme.palette.primary.main,
                  color: isTeamsActive ? theme.palette.success.main : theme.palette.primary.main,
                  backgroundColor: isTeamsActive ? alpha(theme.palette.success.light, 0.12) : 'transparent',
                  '&:hover': {
                    borderColor: isTeamsActive ? theme.palette.success.main : theme.palette.primary.dark,
                    backgroundColor: isTeamsActive
                      ? alpha(theme.palette.success.light, 0.12)
                      : alpha(theme.palette.primary.light, 0.12),
                  },
                })}
                startIcon={
                  <Box
                    component="img"
                    src="/images/apps/teams.png"
                    alt="Teams"
                    sx={{
                      width: 24,
                      height: 24,
                      minWidth: 24,
                      minHeight: 24,
                      maxWidth: 24,
                      maxHeight: 24,
                      objectFit: 'contain',
                      display: 'block'
                    }}
                  />
                }
              >
                {isTeamsActive ? '✓ Teams' : 'Teams'}
              </Button>
            </span>}
          </Box>

          {/* Channel Name (for Regular) or WhatsApp Connection Form */}
          {selectedMessagingProvider === 'regular' && (
            <Box>
              <Typography variant="body2" sx={{ mb: 2, fontWeight: 500, color: 'text.primary' }}>
                What is the name of this channel?{' '}
                <Box component="span" sx={{ color: 'error.main', fontSize: '1.2em', fontWeight: 600 }}>*</Box>
              </Typography>
              <TextField
                data-testid="channel-name-input"
                fullWidth
                label={`${LanguageConstants.topic} Name`}
                value={channelName}
                onChange={(e) => setChannelName(e.target.value)}
                placeholder="e.g., Customer Support, Sales Team"
                required
                autoFocus
                disabled={isChannelCreated}
                InputLabelProps={{
                  required: false,
                }}
              />
            </Box>
          )}

          {/* Authentication Success Message - Only for messaging providers (hide when channel is created) */}
          {(selectedMessagingProvider === 'whatsapp' || selectedMessagingProvider === 'slack' ||
            selectedMessagingProvider === 'google_chat' || selectedMessagingProvider === 'google_chat_inbox' ||
            selectedMessagingProvider === 'teams') &&
            messagingConnectionTested && !createdChannelId && !messagingConnectionData?.channel_id && (
            <Alert 
              data-testid="add-space-modal-messaging-auth-success-alert"
              severity="success" 
              icon={<CheckCircle />}
              sx={{ 
                borderRadius: 2,
                '& .MuiAlert-message': {
                  width: '100%'
                }
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }} data-testid="add-space-modal-messaging-auth-success-content">
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1 }} data-testid="add-space-modal-messaging-auth-success-message">
                  <Typography variant="body1" sx={{ fontWeight: 600 }} data-testid="add-space-modal-messaging-auth-success-title">
                    Authentication Successful!
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }} data-testid="add-space-modal-messaging-auth-success-description">
                    {selectedMessagingProvider === 'whatsapp' ? 'WhatsApp' :
                     selectedMessagingProvider === 'slack' ? 'Slack' :
                     selectedMessagingProvider === 'google_chat' ? 'Google Chat' :
                     selectedMessagingProvider === 'google_chat_inbox' ? 'Google Chat Inbox' :
                     selectedMessagingProvider === 'teams' ? 'Microsoft Teams' : 'Provider'} account has been authenticated successfully.
                  </Typography>
                </Box>
                <Button
                  data-testid="add-space-modal-change-messaging-account-button"
                  variant="contained"
                  onClick={handleRemoveMessagingAccount}
                  sx={{ 
                    ml: 2, 
                    flexShrink: 0
                  }}
                >
                  Change account
                </Button>
              </Box>
            </Alert>
          )}

          {/* Workspace Section — hidden for message channels (makind_workspace_as_default
              branch): workspace is auto-selected to the current company's own default
              workspace via the effect near the top of this component instead of being picked
              here. spaceData.workspaceTag/workspaceId are still populated the same way
              (handleWorkspaceTagChange), so isStepValid and the create-channel payload below
              are unaffected. */}

          {/* Description Section — no longer carries the required asterisk; the backend's
              actual new-channel gate is channel_business_rules (below), not description. */}
          <Box>
            <Typography variant="body2" sx={{ mb: 2, fontWeight: 500, color: 'text.primary' }}>
              What is the purpose of this channel?
            </Typography>
            <Tooltip
              title={isChannelCreated ? "Delete the connection and try again" : ""}
              arrow
              placement="top"
            >
              <TextField
                fullWidth
                label="Description"
                value={spaceData.description}
                onChange={(e) => handleSpaceDataChange('description', e.target.value)}
                placeholder="e.g, This channel is for HR support shared email inbox"
                multiline
                rows={4}
                required
                disabled={isChannelCreated}
                InputLabelProps={{
                  required: false,
                }}
              />
            </Tooltip>
          </Box>

          {/* Business Rules — shown after description is entered; auto-fetch runs via suggestTrigger="auto".
              required marks this as the field the backend now actually enforces on new-channel
              creation (channel_business_rules); not required once reconnecting to an existing
              channel (isChannelCreated true), matching the backend's own reconnect exemption. */}
          {spaceData.description.trim() !== '' && (
            <>
              <BusinessRulesSection
                variant="edit"
                saveMode="deferred"
                suggestTrigger="auto"
                channelType="message"
                channelSubType={selectedMessagingProvider ?? undefined}
                channelId={createdChannelId || messagingConnectionData?.channel_id || ''}
                workspaceName={showNewTagInput ? newTagName.trim() : spaceData.workspaceTag}
                channelDescription={spaceData.description}
                emailId={
                  selectedMessagingProvider === 'regular' && channelName.trim()
                    ? channelName.trim()
                    : selectedMessagingProvider === 'whatsapp' && persistedMessagingCredentials.whatsapp.phone_number_id
                    ? persistedMessagingCredentials.whatsapp.phone_number_id
                    : getLoggedInUserData()?.email_id || ''
                }
                value={businessRules}
                onRulesChange={(rules) => {
                  setBusinessRules(rules);
                  if (businessRulesServerError) setBusinessRulesServerError('');
                }}
                isChannelAdmin
                enableExpandInAuto
                required={!isChannelCreated}
                // See_workflow_tab_button_is_not_shown_in_message_channel — the real root cause of
                // the button still not appearing after the auto-create effect was extended to
                // 'message': this instance never reported its validation status back up, so
                // businessRulesCanProceed (which that effect gates on) stayed false forever for
                // messaging, no matter how valid the rules were. The email instance below already
                // passes all three of these; mirrored here for parity.
                externalValidationResult={serverValidationResult}
                onValidationMetaChange={handleBusinessRulesValidationMetaChange}
                onSuggestingChange={setIsBusinessRulesSuggesting}
              />
              {businessRulesServerError && (
                <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: -1.5 }}>
                  {businessRulesServerError}
                </Typography>
              )}
              {/* See_workflow_tab_button_is_not_shown_in_message_channel — mirrors the email step's
                  identical block above: once the channel exists, let the admin preview/edit the
                  parsed canvas without leaving onboarding. This previously only existed for email;
                  message channels require business rules and get a real Business Rules Workflow
                  tab post-creation same as email, so there was no reason this preview should be
                  email-only — it was simply never added here. Gated on the channel id the same way
                  the channelId prop above already is (createdChannelId OR
                  messagingConnectionData?.channel_id), so it also covers the reconnect path. */}
              {isChannelCreated && (
                <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                  <Button
                    data-testid="add-space-modal-see-workflow-button-message"
                    variant="outlined"
                    size="small"
                    startIcon={<WorkflowCanvasIcon />}
                    onClick={() => setShowWorkflowPreview(true)}
                  >
                    See workflow canvas
                  </Button>
                  <Typography variant="caption" color="text.secondary">
                    You can also edit this workflow anytime later from Channel Settings.
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Box>
      );
    }

    if (selectedChannelType === 'voice' && step === 0) {
      const isChannelCreated = !!createdChannelId;
      return (
        <Box 
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            py: 2,
          }}
          data-testid="add-space-modal-voice-channel-number-step"
        >
          <TextField
            data-testid="voice-channel-number-input"
            fullWidth
            label="Voice Channel Number"
            value={voiceChannelNumber}
            onChange={(e) => setVoiceChannelNumber(e.target.value)}
            placeholder="e.g., +1234567890"
            required
            autoFocus
            disabled={isChannelCreated}
          />

          {/* Workspace Section */}
          <Box>
            <Typography variant="body2" sx={{ mb: 2, fontWeight: 500, color: 'text.primary' }}>
              What workspace group does this channel belong to?{' '}
              <Box component="span" sx={{ color: 'error.main', fontSize: '1.2em', fontWeight: 600 }}>*</Box>
            </Typography>
            {isChannelCreated ? (
              <Tooltip title="Delete the connection and try again" arrow placement="top">
                <WorkspaceTagAutocomplete
                  value={showNewTagInput ? null : spaceData.workspaceTag || null}
                  onChange={handleWorkspaceTagChange}
                  label="Workspace"
                  placeholder={`e.g., Sales, Support, Marketing`}
                  required
                  disabled={isChannelCreated}
                  showAddNew={true}
                  onAddNew={handleAddNewWorkspaceTag}
                />
              </Tooltip>
            ) : (
              <WorkspaceTagAutocomplete
                value={showNewTagInput ? null : spaceData.workspaceTag || null}
                onChange={handleWorkspaceTagChange}
                label="Workspace"
                placeholder={`e.g., Sales, Support, Marketing`}
                required
                disabled={isChannelCreated}
                showAddNew={true}
                onAddNew={handleAddNewWorkspaceTag}
              />
            )}

            {showNewTagInput && (
              isChannelCreated ? (
                <Tooltip title="Delete the connection and try again" arrow placement="top">
                  <TextField
                    fullWidth
                    label={`New Workspace Name`}
                    value={newTagName}
                    onChange={(e) => handleNewTagNameChange(e.target.value)}
                    placeholder={`Enter new workspace ${LanguageConstants.group.toLowerCase()} name`}
                    required
                    disabled={isChannelCreated}
                    sx={{ mt: 2 }}
                  />
                </Tooltip>
              ) : (
                <TextField
                  fullWidth
                  label={`New Workspace Name`}
                  value={newTagName}
                  onChange={(e) => handleNewTagNameChange(e.target.value)}
                  placeholder={`Enter new workspace ${LanguageConstants.group.toLowerCase()} name`}
                  required
                  disabled={isChannelCreated}
                  sx={{ mt: 2 }}
                />
              )
            )}
          </Box>

          {/* Description Section */}
          <Box>
            <Typography variant="body2" sx={{ mb: 2, fontWeight: 500, color: 'text.primary' }}>
              What is the purpose of this channel?{' '}
              <Box component="span" sx={{ color: 'error.main', fontSize: '1.2em', fontWeight: 600 }}>*</Box>
            </Typography>
            <Tooltip 
              title={isChannelCreated ? "Delete the connection and try again" : ""} 
              arrow
              placement="top"
            >
              <TextField
                fullWidth
                label="Description"
                value={spaceData.description}
                onChange={(e) => handleSpaceDataChange('description', e.target.value)}
                placeholder="e.g, This channel is for HR support shared email inbox"
                multiline
                rows={4}
                required
                disabled={isChannelCreated}
                InputLabelProps={{
                  required: false,
                }}
              />
            </Tooltip>
          </Box>
        </Box>
      );
    }

    // For email channels, adjust step numbers (email connection is step 0, signature is step 1, etc.)
    // For message/voice channels, adjust step numbers
    let adjustedStep = step;
    if (selectedChannelType === 'email') {
      // Email: step 0 = email connection, step 1 = signature, step 2 = data sources, step 3 = apps
      adjustedStep = step;
    } else if (selectedChannelType === 'message') {
      // Message: step 0 = provider (handled above); steps 1-3 = data sources, apps, skills
      adjustedStep = step > 0 ? step - 1 : -1;
    } else if (selectedChannelType === 'voice') {
      // Voice: step 0 = voice number (handled above), step 1 = data sources, step 2 = apps
      adjustedStep = step > 0 ? step - 1 : -1; // Use -1 to indicate step 0 is handled above
    }

    switch (adjustedStep) {
      case -1: {
        // Step 0 for message/voice channels is handled above, return null here
        return null;
      }
      case 0: {
        // For email: Email Connection step
        // For message/voice: Data Sources step (adjustedStep 0 = activeStep 1)
        if (selectedChannelType === 'email') {
          // Email channels: Step 0 = Email Connection
        
        // Show email connection options (enabled or disabled based on whether channel is created)
        const isChannelCreated = !!createdChannelId;
        const isAuthenticated = !!oauthData && oauthData?.provider && !isChannelCreated;
        const connectedEmail = selectedEmailProvider === 'other' 
          ? otherEmailCredentials.emailId 
          : oauthData?.email_id;
        const providerName = selectedEmailProvider === 'gmail' 
          ? 'Gmail' 
          : selectedEmailProvider === 'office365' 
          ? 'Office 365' 
          : 'Email';

        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }} data-testid="add-space-modal-step-0-content">
            {/* <Typography variant="h6" sx={{ mb: 2 }}>
              Email Connection
            </Typography> */}
            
            <Box data-testid="add-space-modal-email-providers-container">
              {/* <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
                Connect Email Account
              </Typography> */}
              <Box sx={{ display: 'flex', gap: 2 }} data-testid="add-space-modal-email-provider-buttons">
                {/* Gmail: shown when marketplace image type is not 'outlook' */}
                {SHOW_GMAIL_FEATURES && (
                  <Tooltip 
                    title={
                      isChannelCreated && selectedEmailProvider !== 'gmail' 
                        ? "Delete the connection and try again" 
                        : isAuthenticated && oauthData?.provider !== 'gmail'
                        ? "Click on 'Change account' to switch providers"
                        : ""
                    } 
                    arrow
                    placement="top"
                  >
                    <span style={{ width: '100%' }}>
                      <Button
                        data-testid="add-space-modal-gmail-button"
                        variant="outlined"
                        fullWidth
                        onClick={handleGmailOAuth}
                        disabled={gmailLoading || (isChannelCreated && selectedEmailProvider !== 'gmail') || (isAuthenticated && oauthData?.provider !== 'gmail')}
                        sx={(theme) => ({
                          py: 2,
                          borderColor: selectedEmailProvider === 'gmail' ? theme.palette.success.main : oauthData?.provider === 'gmail' ? theme.palette.success.main : theme.palette.primary.main,
                          color: selectedEmailProvider === 'gmail' ? theme.palette.success.main : oauthData?.provider === 'gmail' ? theme.palette.success.main : theme.palette.primary.main,
                          backgroundColor: selectedEmailProvider === 'gmail' ? theme.palette.success.light + '20' : oauthData?.provider === 'gmail' ? theme.palette.success.light + '20' : 'transparent',
                          '&:hover': {
                            borderColor: selectedEmailProvider === 'gmail' ? theme.palette.success.main : oauthData?.provider === 'gmail' ? theme.palette.success.main : theme.palette.primary.dark,
                            backgroundColor: selectedEmailProvider === 'gmail' ? theme.palette.success.light + '20' : oauthData?.provider === 'gmail' ? theme.palette.success.light + '20' : theme.palette.primary.light + '20'
                          }
                        })}
                        startIcon={
                          gmailLoading ? (
                            <CircularProgress size={16} />
                          ) : (
                            <Box
                              component="img"
                              src="/images/apps/gmail.png"
                              alt="Gmail"
                              sx={{ width: 24, height: 24 }}
                            />
                          )
                        }
                      >
                        {gmailLoading ? 'Authenticating...' : createdChannelId && oauthData?.provider === 'gmail' ? '✓ Gmail Connected' : oauthData?.provider === 'gmail' ? '✓ Gmail Authenticated' : 'Gmail'}
                      </Button>
                    </span>
                  </Tooltip>
                )}
                {/* Office 365: shown when marketplace image type is not 'gmail' */}
                {SHOW_OUTLOOK_FEATURES && (
                  <Tooltip 
                    title={
                      isChannelCreated && selectedEmailProvider !== 'office365' 
                        ? "Delete the connection and try again" 
                        : isAuthenticated && oauthData?.provider !== 'office365'
                        ? "Click on 'Change account' to switch providers"
                        : ""
                    } 
                    arrow
                    placement="top"
                  >
                    <span style={{ width: '100%' }}>
                      <Button
                        data-testid="add-space-modal-office365-button"
                        variant="outlined"
                        fullWidth
                        onClick={handleOffice365CardClick}
                        disabled={office365Loading || (isChannelCreated && selectedEmailProvider !== 'office365') || (isAuthenticated && oauthData?.provider !== 'office365')}
                        sx={(theme) => ({
                          py: 2,
                          borderColor: selectedEmailProvider === 'office365' ? theme.palette.success.main : oauthData?.provider === 'office365' ? theme.palette.success.main : theme.palette.primary.main,
                          color: selectedEmailProvider === 'office365' ? theme.palette.success.main : oauthData?.provider === 'office365' ? theme.palette.success.main : theme.palette.primary.main,
                          backgroundColor: selectedEmailProvider === 'office365' ? theme.palette.success.light + '20' : oauthData?.provider === 'office365' ? theme.palette.success.light + '20' : 'transparent',
                          '&:hover': {
                            borderColor: selectedEmailProvider === 'office365' ? theme.palette.success.main : oauthData?.provider === 'office365' ? theme.palette.success.main : theme.palette.primary.dark,
                            backgroundColor: selectedEmailProvider === 'office365' ? theme.palette.success.light + '20' : oauthData?.provider === 'office365' ? theme.palette.success.light + '20' : theme.palette.primary.light + '20'
                          }
                        })}
                        startIcon={
                          office365Loading ? (
                            <CircularProgress size={16} />
                          ) : (
                            <Box
                              component="img"
                              src="/images/apps/outlook-v2.png"
                              alt="Office 365"
                              sx={{ width: 24, height: 24 }}
                            />
                          )
                        }
                      >
                        {office365Loading ? 'Authenticating...' : createdChannelId && oauthData?.provider === 'office365' ? '✓ Office 365 Connected' : oauthData?.provider === 'office365' ? '✓ Office 365 Authenticated' : 'Office 365'}
                      </Button>
                    </span>
                  </Tooltip>
                )}
                <Tooltip 
                  title={
                    isChannelCreated && selectedEmailProvider !== 'other' 
                      ? "Delete the connection and try again" 
                      : isAuthenticated
                      ? "Click on 'Change account' to switch providers"
                      : ""
                  } 
                  arrow
                  placement="top"
                >
                  <span style={{ width: '100%' }}>
                    <Button
                      data-testid="add-space-modal-other-email-button"
                      variant="outlined"
                      fullWidth
                      disabled={(isChannelCreated && selectedEmailProvider !== 'other') || isAuthenticated}
                      sx={(theme) => ({
                        py: 2,
                        borderColor: selectedEmailProvider === 'other' ? theme.palette.success.main : theme.palette.primary.main,
                        color: selectedEmailProvider === 'other' ? theme.palette.success.main : theme.palette.primary.main,
                        backgroundColor: selectedEmailProvider === 'other' ? theme.palette.success.light + '20' : 'transparent',
                        '&:hover': {
                          borderColor: selectedEmailProvider === 'other' ? theme.palette.success.main : theme.palette.primary.dark,
                          backgroundColor: selectedEmailProvider === 'other' ? theme.palette.success.light + '20' : theme.palette.primary.light + '20'
                        }
                      })}
                      startIcon={
                        <Box
                          component="img"
                          src="/images/apps/email.png"
                          alt="Other Email"
                          sx={{ width: 24, height: 24 }}
                        />
                      }
                      onClick={() => {
                        setSelectedEmailProvider('other');
                        setError(null); // Clear any previous errors when selecting Other email
                      }}
                    >
                      Other Email
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            </Box>

            {/* Spam automation toggle — same channel_settings.spam_auto_push_enabled contract as
                EmailConfigTab's toggle. Before the channel exists, toggling just updates local
                state (applied by persistSpamAutoPushSetting inside createChannel() the moment the
                channel is created); after the channel exists, toggling persists immediately via
                handleSpamAutoPushToggle. Shown regardless of which provider is chosen. */}
            <Box
              data-testid="add-space-modal-spam-auto-push-container"
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                mt: 2,
                p: 2,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Automatically move detected spam
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Prism7 will move emails it classifies as spam to your provider's Spam/Junk folder.
                </Typography>
              </Box>
              <Switch
                data-testid="add-space-modal-spam-auto-push-switch"
                checked={spamAutoPushOnCreate}
                disabled={isPersistingSpamAutoPush}
                onChange={(e) => handleSpamAutoPushToggle(e.target.checked, createdChannelId)}
                // MUI v7: Switch always overrides the legacy inputProps prop with its own
                // slotProps.input (role: 'switch') when forwarding to SwitchBase — inputProps
                // here would be silently dropped. slotProps.input is what actually reaches the
                // rendered <input>, but it REPLACES (not merges with) MUI's default
                // { role: 'switch' }, so role must be repeated here too.
                slotProps={{ input: { 'aria-label': 'Automatically move detected spam', role: 'switch' } }}
              />
            </Box>

            {/* Connection Details - Show when channel is created */}
            {isChannelCreated && (
              <Alert 
                data-testid="add-space-modal-connection-success-alert"
                severity="success" 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  '& .MuiAlert-message': {
                    width: '100%'
                  }
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }} data-testid="add-space-modal-connection-details">
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1 }} data-testid="add-space-modal-connected-email">
                    {connectedEmail}
                  </Typography>
                  <Button
                    data-testid="add-space-modal-delete-connection-button"
                    variant="outlined"
                    color="error"
                    size="small"
                    startIcon={loading ? <CircularProgress size={16} /> : <DeleteIcon />}
                    onClick={handleDeleteConnection}
                    disabled={loading}
                    sx={{ ml: 2, flexShrink: 0 }}
                  >
                    {loading ? 'Deleting...' : 'Delete Connection'}
                  </Button>
                </Box>
              </Alert>
            )}

            {/* Other Emails Configuration */}
            {selectedEmailProvider === 'other' && (
              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  // Was #fafafa / #e0e0e0 — light-only; dark mode needs dark surface + readable labels
                  backgroundColor:
                    muiTheme.palette.mode === 'dark'
                      ? muiTheme.palette.grey[900]
                      : muiTheme.palette.grey[50],
                }}
                data-testid="add-space-modal-other-email-config"
              >
                {/* Email Credentials Section */}
                <Box sx={{ display: 'flex', gap: 2, mb: 3 }} data-testid="add-space-modal-email-credentials">
                  <TextField
                    data-testid="add-space-modal-other-email-id-input"
                    label="Email ID"
                    placeholder="your-email@example.com"
                    fullWidth
                    required
                    value={otherEmailCredentials.emailId}
                    onChange={(e) => handleOtherEmailCredentialsChange('emailId', e.target.value)}
                    sx={{ flex: 1 }}
                    InputLabelProps={{
                      required: true,
                      sx: {
                        '& .MuiFormLabel-asterisk': {
                          color: 'error.main',
                          fontSize: '1.2em',
                          fontWeight: 600,
                        },
                      },
                    }}
                  />
                  <TextField
                    data-testid="add-space-modal-other-email-password-input"
                    label="Password"
                    type="password"
                    placeholder="Your email password"
                    fullWidth
                    required
                    value={otherEmailCredentials.password}
                    onChange={(e) => handleOtherEmailCredentialsChange('password', e.target.value)}
                    sx={{ flex: 1 }}
                    InputLabelProps={{
                      required: true,
                      sx: {
                        '& .MuiFormLabel-asterisk': {
                          color: 'error.main',
                          fontSize: '1.2em',
                          fontWeight: 600,
                        },
                      },
                    }}
                  />
                </Box>

                <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold', color: 'text.primary', fontSize: '1rem', lineHeight: '1' }} data-testid="add-space-modal-imap-section-title">
                   Incoming Mail Server (IMAP)
                 </Typography>
                 <Box sx={{ display: 'flex', gap: 2, mb: 3 }} data-testid="add-space-modal-imap-server-config">
                 <TextField
                     data-testid="add-space-modal-imap-server-input"
                     label="Server Name"
                     placeholder="imap.server.com"
                     fullWidth
                     required
                     value={otherEmailCredentials.imapServer}
                     onChange={(e) => handleOtherEmailCredentialsChange('imapServer', e.target.value)}
                     sx={{ flex: 2 }}
                     InputLabelProps={{
                       required: true,
                       sx: {
                         '& .MuiFormLabel-asterisk': {
                           color: 'error.main',
                           fontSize: '1.2em',
                           fontWeight: 600,
                         },
                       },
                     }}
                   />
                   <TextField
                     data-testid="add-space-modal-imap-port-input"
                     label="Port"
                     placeholder="993"
                     value={otherEmailCredentials.imapPort}
                     onChange={(e) => handleOtherEmailCredentialsChange('imapPort', e.target.value)}
                     fullWidth
                     required
                     sx={{ flex: 1 }}
                     InputLabelProps={{
                       required: true,
                       sx: {
                         '& .MuiFormLabel-asterisk': {
                           color: 'error.main',
                           fontSize: '1.2em',
                           fontWeight: 600,
                         },
                       },
                     }}
                   />
                 </Box>
                 <Box sx={{ display: 'flex', gap: 2, mb: 3 }} data-testid="add-space-modal-imap-security-config">
                   <FormControl fullWidth sx={{ flex: 1 }}>
                     <InputLabel>Require SSL</InputLabel>
                     <Select 
                       data-testid="add-space-modal-imap-ssl-select"
                       value={otherEmailCredentials.imapSsl} 
                       label="Require SSL"
                       onChange={(e) => handleOtherEmailCredentialsChange('imapSsl', e.target.value)}
                     >
                       <MenuItem value="yes">Yes</MenuItem>
                       <MenuItem value="no">No</MenuItem>
                     </Select>
                   </FormControl>
                   <FormControl fullWidth sx={{ flex: 1 }}>
                     <InputLabel>Connection Security</InputLabel>
                     <Select 
                       data-testid="add-space-modal-imap-security-select"
                       value={otherEmailCredentials.imapSecurity} 
                       label="Connection Security"
                       onChange={(e) => handleOtherEmailCredentialsChange('imapSecurity', e.target.value)}
                     >
                       <MenuItem value="ssl">SSL</MenuItem>
                       <MenuItem value="tls">TLS</MenuItem>
                       <MenuItem value="none">None</MenuItem>
                     </Select>
                   </FormControl>
                 </Box>

                 <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold', color: 'text.primary', fontSize: '1rem', lineHeight: '1' }} data-testid="add-space-modal-smtp-section-title">
                   Outgoing Mail Server
                 </Typography>
                 <Box sx={{ display: 'flex', gap: 2, mb: 3 }} data-testid="add-space-modal-smtp-server-config">
                   <TextField
                     data-testid="add-space-modal-smtp-server-input"
                     label="Server Name"
                     placeholder="smtp.server.com"
                     fullWidth
                     required
                     value={otherEmailCredentials.smtpServer}
                     onChange={(e) => handleOtherEmailCredentialsChange('smtpServer', e.target.value)}
                     sx={{ flex: 2 }}
                     InputLabelProps={{
                       required: true,
                       sx: {
                         '& .MuiFormLabel-asterisk': {
                           color: 'error.main',
                           fontSize: '1.2em',
                           fontWeight: 600,
                         },
                       },
                     }}
                   />
                   <TextField
                     data-testid="add-space-modal-smtp-port-input"
                     label="Port"
                     placeholder="587"
                     value={otherEmailCredentials.smtpPort}
                     onChange={(e) => handleOtherEmailCredentialsChange('smtpPort', e.target.value)}
                     fullWidth
                     required
                     sx={{ flex: 1 }}
                     InputLabelProps={{
                       required: true,
                       sx: {
                         '& .MuiFormLabel-asterisk': {
                           color: 'error.main',
                           fontSize: '1.2em',
                           fontWeight: 600,
                         },
                       },
                     }}
                   />
                 </Box>
                 <Box sx={{ display: 'flex', gap: 2, mb: 3 }} data-testid="add-space-modal-smtp-security-config">
                   <FormControl fullWidth sx={{ flex: 1 }}>
                     <InputLabel>Requires Authentication</InputLabel>
                     <Select 
                       data-testid="add-space-modal-smtp-auth-select"
                       value={otherEmailCredentials.smtpAuth} 
                       label="Requires Authentication"
                       onChange={(e) => handleOtherEmailCredentialsChange('smtpAuth', e.target.value)}
                     >
                       <MenuItem value="yes">Yes</MenuItem>
                       <MenuItem value="no">No</MenuItem>
                     </Select>
                   </FormControl>
                   <FormControl fullWidth sx={{ flex: 1 }}>
                     <InputLabel>Require SSL</InputLabel>
                     <Select 
                       data-testid="add-space-modal-smtp-ssl-select"
                       value={otherEmailCredentials.smtpSsl} 
                       label="Require SSL"
                       onChange={(e) => handleOtherEmailCredentialsChange('smtpSsl', e.target.value)}
                     >
                       <MenuItem value="yes">Yes</MenuItem>
                       <MenuItem value="no">No</MenuItem>
                     </Select>
                   </FormControl>
                   <FormControl fullWidth sx={{ flex: 1 }}>
                     <InputLabel>Connection Security</InputLabel>
                     <Select 
                       data-testid="add-space-modal-smtp-security-select"
                       value={otherEmailCredentials.smtpSecurity} 
                       label="Connection Security"
                       onChange={(e) => handleOtherEmailCredentialsChange('smtpSecurity', e.target.value)}
                     >
                       <MenuItem value="tls">TLS/STARTTLS</MenuItem>
                       <MenuItem value="ssl">SSL</MenuItem>
                       <MenuItem value="none">None</MenuItem>
                     </Select>
                   </FormControl>
                 </Box>

                 {/* Test Connection Button */}
                 <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }} data-testid="add-space-modal-test-connection-container">
                   <Button
                     data-testid="add-space-modal-test-connection-button"
                     variant="outlined"
                     onClick={handleTestConnection}
                     disabled={!otherEmailCredentials.emailId || !otherEmailCredentials.password || testingConnection}
                     startIcon={testingConnection ? <CircularProgress size={16} /> : null}
                     sx={{
                       borderColor: connectionTested ? 'success.main' : 'primary.main',
                       color: connectionTested ? 'success.main' : 'primary.main',
                       '&:hover': {
                         borderColor: connectionTested ? 'success.main' : 'primary.main',
                         backgroundColor: connectionTested
                           ? alpha(muiTheme.palette.success.main, 0.08)
                           : alpha(muiTheme.palette.primary.main, 0.08),
                       },
                     }}
                   >
                     {testingConnection ? 'Testing...' : connectionTested ? '✓ Connection Successful' : 'Test Connection'}
                   </Button>
                 </Box>
              </Box>
            )}

            {/* Authentication Success Message - Only for Gmail and Office 365 (hide when connected) */}
            {oauthData && oauthData?.provider && (selectedEmailProvider === 'gmail' || selectedEmailProvider === 'office365') && !createdChannelId && (
              <Alert 
                data-testid="add-space-modal-auth-success-alert"
                severity="success" 
                icon={<CheckCircle />}
                sx={{ 
                  borderRadius: 2,
                  '& .MuiAlert-message': {
                    width: '100%'
                  }
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }} data-testid="add-space-modal-auth-success-content">
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1 }} data-testid="add-space-modal-auth-success-message">
                    <Typography variant="body1" sx={{ fontWeight: 600 }} data-testid="add-space-modal-auth-success-title">
                      Authentication Successful!
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }} data-testid="add-space-modal-auth-success-description">
                      {providerName} account <strong>{connectedEmail}</strong> has been authenticated successfully.
                    </Typography>
                  </Box>
                  <Button
                    data-testid="add-space-modal-change-account-button"
                    variant="contained"
                    onClick={handleChangeAccount}
                    sx={{ 
                      ml: 2, 
                      flexShrink: 0
                    }}
                  >
                    Change account
                  </Button>
                </Box>
              </Alert>
            )}
            
            {/* Workspace Section — hidden for email channels (makind_workspace_as_default
                branch): workspace is auto-selected to the current company's own default
                workspace via the effect near the top of this component instead of being picked
                here. spaceData.workspaceTag/workspaceId are still populated the same way
                (handleWorkspaceTagChange), so isStepValid and the create-channel payload below
                are unaffected. */}

            {/* Tags & Sub tags Section — one row per tag */}
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ mb: 2, fontWeight: 500, color: 'text.primary' }}>
                Select tags &amp; sub tags for this channel{' '}
                <Box component="span" sx={{ color: 'error.main', fontSize: '1.2em', fontWeight: 600 }}>*</Box>
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {tagRows.map((row, rowIndex) => {
                  const rowTag = row.tag;
                  const availableOptions = TAG_OPTIONS.filter(
                    opt => !tagRows.some((r, i) => i !== rowIndex && r.tag === opt)
                  );
                  return (
                    <Box key={row.id} sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: 2 }}>
                      <Box sx={{ width: 220, flexShrink: 0 }}>
                        <Autocomplete
                          options={availableOptions}
                          value={rowTag || null}
                          onChange={(_, newValue) => {
                            const newRows = [...tagRows];
                            newRows[rowIndex] = { ...newRows[rowIndex], tag: newValue ?? '' };
                            setTagRows(newRows);
                          }}
                          renderInput={(params) => (
                            <TextField {...params} label="Select tag" />
                          )}
                          sx={{
                            '& .MuiInputBase-root': { cursor: 'text' },
                            '& .MuiAutocomplete-input': { cursor: 'text' },
                          }}
                        />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        {rowTag ? (
                          <KeywordsMultiSelect
                            value={selectedKeywordsPerTag[rowTag] ?? []}
                            onChange={(tags) =>
                              setSelectedKeywordsPerTag((prev) => ({ ...prev, [rowTag]: tags }))
                            }
                            workspaceName={showNewTagInput ? newTagName.trim() : spaceData.workspaceTag}
                            emailId={selectedEmailProvider === 'other'
                              ? otherEmailCredentials.emailId
                              : oauthData?.email_id || ''}
                            categories={selectedCategories}
                            tag={rowTag}
                            disabled={false}
                            placeholder={`Add sub tags for ${rowTag}…`}
                            label={`Sub tags for ${rowTag}`}
                            data-testid={`keywords-multi-select-${rowTag}`}
                            lazyFetch
                          />
                        ) : (
                          <TextField
                            disabled
                            fullWidth
                            label="Select a tag first"
                            sx={{ '& .MuiInputBase-root': { cursor: 'default' } }}
                          />
                        )}
                      </Box>
                      {tagRows.length > 1 && (
                        <IconButton
                          size="small"
                          onClick={() => {
                            const newRows = tagRows.filter((_, i) => i !== rowIndex);
                            setTagRows(newRows.length > 0 ? newRows : [{ id: crypto.randomUUID(), tag: '' }]);
                          }}
                          sx={{ mt: 1 }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  );
                })}
              </Box>
              <Button
                startIcon={<AddIcon />}
                size="small"
                variant="text"
                onClick={() => setTagRows(prev => [...prev, { id: crypto.randomUUID(), tag: '' }])}
                disabled={tagRows.some(r => r.tag === '') || tagRows.filter(r => r.tag).length >= TAG_OPTIONS.length}
                sx={{ mt: 1 }}
              >
                Add more tags
              </Button>
            </Box>

            {/* Description Section - Only for email channels */}
            {(showDescriptionField || showDescriptionCards || loadingDescriptions || spaceData.description.trim() !== '') && (
              <Box 
                ref={descriptionSectionRef}
                sx={{
                  minHeight: loadingDescriptions ? '250px' : 'auto',
                  height: loadingDescriptions && descriptionSuggestions.length === 0 ? '250px' : 'auto',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                    {showDescriptionCards && !showDescriptionField
                      ? 'Select a description for your channel'
                      : 'Purpose of the Channel'}
                  </Typography>
                  {/* View Suggestions button is always enabled on step 0, even after channel creation */}
                  {descriptionSuggestions.length > 0 && spaceData.description.trim() !== '' && (
                    <Button
                      variant="text"
                      size="small"
                      onClick={handleToggleDescriptionView}
                      disabled={false}
                      sx={{ textTransform: 'none', minWidth: 'auto' }}
                    >
                      {showDescriptionField ? 'View Suggestions' : 'Edit Description'}
                    </Button>
                  )}
                </Box>
                
                {/* Description field is always editable on step 0, even after channel creation */}
                {showDescriptionField && !showDescriptionCards ? (
                  <Box sx={{ position: 'relative' }}>
                    <TextField
                      data-testid="add-space-modal-description-input"
                      fullWidth
                      label="Description"
                      value={spaceData.description}
                      onChange={(e) => handleSpaceDataChange('description', e.target.value)}
                      placeholder="e.g., This channel is for the HR support shared email inbox"
                      multiline
                      rows={4}
                      required
                      disabled={false}
                      inputRef={descriptionTextareaRef}
                      InputLabelProps={{
                        required: false,
                      }}
                      sx={{
                        '& .MuiInputBase-inputMultiline': {
                          pr: 4.5,
                        },
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 10,
                        right: descriptionHasVerticalScroll && descriptionScrollbarWidth > 0
                          ? DESCRIPTION_EXPAND_ICON_BASE_RIGHT +
                            descriptionScrollbarWidth +
                            DESCRIPTION_EXPAND_ICON_SCROLL_GAP
                          : DESCRIPTION_EXPAND_ICON_BASE_RIGHT,
                        zIndex: 1,
                      }}
                    >
                      <Tooltip title="Expand">
                        <IconButton
                          onClick={() => setIsDescriptionExpanded(true)}
                          size="small"
                          aria-label="Expand description"
                          sx={{
                            color: 'text.secondary',
                            '&:hover': {
                              color: 'primary.main',
                              backgroundColor: 'action.hover',
                            },
                          }}
                        >
                          <OpenInFullIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                ) : (
                  (showDescriptionCards || loadingDescriptions) && (
                    <DescriptionCards
                      descriptions={descriptionSuggestions}
                      loading={loadingDescriptions}
                      selectedDescription={spaceData.description}
                      onSelect={handleDescriptionSelect}
                      onEdit={handleDescriptionEdit}
                      disabled={false}
                    />
                  )
                )}
              </Box>
            )}

            {/* Business Rules — shown only after a description is selected; auto-fetch runs via suggestTrigger="auto".
                required only while creating a NEW channel (createdChannelId not yet set) — the
                backend exempts reconnects from the channel_business_rules requirement.
                maxLength=4000 matches EmailConnectRequest's server-side max_length=4000. */}
            {selectedChannelType === 'email' && spaceData.description.trim() !== '' && (
              <>
                <BusinessRulesSection
                  variant="edit"
                  saveMode="deferred"
                  suggestTrigger="auto"
                  channelId={createdChannelId}
                  workspaceName={showNewTagInput ? newTagName.trim() : spaceData.workspaceTag}
                  channelDescription={spaceData.description}
                  channelTags={selectedCategories}
                  channelSubtags={flatSelectedKeywords}
                  emailId={getConfiguredEmailId()}
                  value={businessRules}
                  onRulesChange={(rules) => {
                    setBusinessRules(rules);
                    if (businessRulesServerError) setBusinessRulesServerError('');
                  }}
                  isChannelAdmin
                  enableExpandInAuto
                  required={!createdChannelId}
                  maxLength={4000}
                  showCharacterCount
                  externalValidationResult={serverValidationResult}
                  onValidationMetaChange={handleBusinessRulesValidationMetaChange}
                  onSuggestingChange={setIsBusinessRulesSuggesting}
                />
                {businessRulesServerError && (
                  <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: -1.5 }}>
                    {businessRulesServerError}
                  </Typography>
                )}
                {/* Odoo #17929 — channel already auto-created once validation succeeded; let the
                    admin preview/edit the parsed canvas without leaving onboarding. Purely
                    optional — "Next" (below) advances regardless of whether this was opened. */}
                {createdChannelId && (
                  <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                    <Button
                      data-testid="add-space-modal-see-workflow-button"
                      variant="outlined"
                      size="small"
                      startIcon={<WorkflowCanvasIcon />}
                      onClick={() => setShowWorkflowPreview(true)}
                    >
                      See workflow canvas
                    </Button>
                    <Typography variant="caption" color="text.secondary">
                      You can also edit this workflow anytime later from Channel Settings.
                    </Typography>
                  </Box>
                )}
              </>
            )}
          </Box>
        );
        } else {
          // Message/Voice channels: adjustedStep 0 = activeStep 1 = Data Sources step
          // Use fallback to messagingConnectionData?.channel_id for message channels
          const channelIdForDataSource = selectedChannelType === 'message' 
            ? (createdChannelId || messagingConnectionData?.channel_id || '')
            : createdChannelId;
          
          return (
            <Box data-testid="add-space-modal-step-1-content">
              <DataSourceStep
                ref={dataSourceStepRef}
                dataSources={spaceData.dataSources}
                onDataSourcesChange={(sources) => handleSpaceDataChange('dataSources', sources)}
                onDataSourceAdded={() => {
                  // Update the spaceData to reflect the successful upload
                  // This will be handled by the handleNext function
                }}
                channelId={channelIdForDataSource}
                hideInternalButtons={true}
                showStepNavigationButtons={true}
              />
            </Box>
          );
        }
      }
      
      case 1: {
        // For email: Signature & Salutation step
        // For message/voice: Apps Selection step (adjustedStep 1 = activeStep 2)
        if (selectedChannelType === 'email') {
          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }} data-testid="add-space-modal-step-1-content">
              {/* Reuse AddSignatureAndSalutationModal component in content-only mode */}
              <Box data-testid="add-space-modal-signature-salutation-container">
                <AddSignatureAndSalutationModal
                  open={false}
                  onClose={() => {}}
                  onSave={() => {}}
                  contentOnly={true}
                  initialData={spaceData.signatureAndSalutation && spaceData.signatureAndSalutation.length > 0 
                    ? spaceData.signatureAndSalutation[0] 
                    : undefined}
                  channelId={createdChannelId}
                  onDataChange={(data) => {
                    const updated = [data];
                    handleSpaceDataChange('signatureAndSalutation', updated);
                  }}
                />
              </Box>
            </Box>
          );
        } else {
          // Message/Voice channels: adjustedStep 1 = activeStep 2 = Apps Selection step
          // Use fallback to messagingConnectionData?.channel_id for message channels
          const channelIdForApps = selectedChannelType === 'message' 
            ? (createdChannelId || messagingConnectionData?.channel_id || '')
            : createdChannelId;
          return (
            <Box data-testid="add-space-modal-step-2-content">
              <AppsSelectionStep
                selectedApps={spaceData.selectedApps}
                onAppsChange={(apps) => handleSpaceDataChange('selectedApps', apps)}
                channelId={channelIdForApps}
              />
            </Box>
          );
        }
      }
      
      case 2:
        // Step 2: Data Sources for email; Connect Skills for message
        if (selectedChannelType === 'email') {
          // Email channels: Step 2 = Data Sources
          return (
            <Box data-testid="add-space-modal-step-2-content">
              <DataSourceStep
                ref={dataSourceStepRef}
                dataSources={spaceData.dataSources}
                onDataSourcesChange={(sources) => handleSpaceDataChange('dataSources', sources)}
                onDataSourceAdded={() => {
                  // Update the spaceData to reflect the successful upload
                  // This will be handled by the handleNext function
                }}
                channelId={createdChannelId}
                hideInternalButtons={true}
                showStepNavigationButtons={true}
              />
            </Box>
          );
        } else if (selectedChannelType === 'message') {
          const channelIdForSkills =
            createdChannelId || messagingConnectionData?.channel_id || '';
          const workspaceTagForSkills =
            stableWorkspaceTag || (showNewTagInput ? newTagName.trim() : spaceData.workspaceTag);

          return (
            <Box data-testid="add-space-modal-step-3-content">
              <SkillsSelectionStep
                channelId={channelIdForSkills}
                workspaceTag={workspaceTagForSkills}
              />
            </Box>
          );
        }

        return null;
      
      case 3:
        // Apps Selection step (email only)
        if (selectedChannelType === 'email') {
          return (
            <Box data-testid="add-space-modal-step-3-content">
              <AppsSelectionStep
                selectedApps={spaceData.selectedApps}
                onAppsChange={(apps) => handleSpaceDataChange('selectedApps', apps)}
                channelId={createdChannelId}
              />
            </Box>
          );
        }

        return null;

      case 4:
        // Skills Selection step (email only — message uses adjustedStep 2)
        if (selectedChannelType !== 'email') {
          return null;
        }

        return (
          <Box data-testid="add-space-modal-step-4-content">
            <SkillsSelectionStep
              channelId={createdChannelId}
              workspaceTag={stableWorkspaceTag}
            />
          </Box>
        );

      case 5:
        // Automation step (email only — message uses adjustedStep 3)
        if (selectedChannelType !== 'email') {
          return null;
        }

        return (
          <Box data-testid="add-space-modal-step-5-content">
            <AutomationTab
              channelId={createdChannelId}
              channelSubtags={flatSelectedKeywords}
              onOpenChannelConfigEdit={() => setActiveStep(0)}
              noSubtagsEmptyStateCopy={NO_SUBTAGS_EMPTY_STATE_EMAIL_SETUP}
            />
          </Box>
        );
      
      default:
        return null;
    }
  };

  return (
    <Dialog
      data-testid="add-space-modal"
      open={open}
      onClose={(event, reason) => {
         handleModalClose(event, reason);
      }}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          ...modalStyles.dialog['& .MuiDialog-paper'],
          '& .MuiDialogContent-root': {
            '@keyframes fadeIn': {
              '0%': { opacity: 0, transform: 'scale(0.8)' },
              '100%': { opacity: 1, transform: 'scale(1)' }
            }
          }
        },
      }}
    >
      <DialogTitle sx={modalStyles.dialogTitle} data-testid="add-space-modal-title">
        <Typography variant="h5" component="div" data-testid="add-space-modal-title-text">
          {showChannelTypeSelection 
            ? `Add ${LanguageConstants.topic}` 
            : selectedChannelType === 'email' 
              ? `Configure email${activeStep >= 1 && activeStep <= 5 ? ` ${renderConnectedMailId()}` : ''}` 
              : selectedChannelType === 'message'
                ? `Configure Messaging ${LanguageConstants.topic}`
                : `Configure Voice ${LanguageConstants.topic}`}
        </Typography>
        <IconButton
          data-testid="add-space-modal-close-button"
          onClick={handleClose}
          sx={modalStyles.closeButton}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent 
        ref={dialogContentRef}
        data-testid="add-space-modal-content"
        sx={{ 
          pt: 0,
          overflow: 'auto',
          maxHeight: '70vh',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(0, 0, 0, 0.2)',
            borderRadius: '4px',
            '&:hover': {
              background: 'rgba(0, 0, 0, 0.3)',
            },
          },
        }}
      >
        {/* When subscription disabled, show validation loading or blocked error on open */}
        {DISABLE_SUBSCRIPTION && subscriptionValidationLoading ? (
          <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : DISABLE_SUBSCRIPTION && subscriptionBlocked ? (
          <Box sx={{ py: 2 }}>
            <Alert severity="info" data-testid="add-space-modal-subscription-blocked">
              {error}
            </Alert>
          </Box>
        ) : showChannelTypeSelection ? (
          <Box sx={{ py: 2 }}>
            <ChannelTypeSelectionView onChannelTypeSelect={handleChannelTypeSelect} />
          </Box>
        ) : (
          <>
            {/* Stepper */}
            {showStepCreation && steps.length > 0 && (
              <Box sx={{ mb: 4, mt: 2 }} data-testid="add-space-modal-stepper-container">
                <Stepper activeStep={activeStep} alternativeLabel data-testid="add-space-modal-stepper">
                  {steps.map((label, index) => (
                    <Step key={label} data-testid={`add-space-modal-step-${index}`}>
                      <StepLabel StepIconComponent={CustomStepIcon} data-testid={`add-space-modal-step-label-${index}`}>{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </Box>
            )}

            {/* Error Alert */}
            {error && (
              <Box ref={errorAlertRef} sx={{ mb: 2 }} data-testid="add-space-modal-error-alert">
                <Alert severity="error">
                  {error}
                </Alert>
              </Box>
            )}

            {/* Step Content */}
            <Box sx={{ minHeight: 300 }} data-testid={`add-space-modal-step-content-${activeStep}`}>
              {renderStepContent(activeStep)}
            </Box>
          </>
        )}
      </DialogContent>

      {/* Office 365 info modal: shown when user clicks Office 365 card; admin link + button to go to OAuth */}
      <Dialog
        open={adminConsentDialogOpen}
        onClose={() => setAdminConsentDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { p: 2 } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Connect Office 365
          <IconButton onClick={() => setAdminConsentDialogOpen(false)} size="small" aria-label="Close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Connect your Office 365 account. If your organization shows &quot;Approval required&quot;, your IT admin must approve the app once. After that, you and your colleagues can connect without seeing that screen again.
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
            Need admin approval? Send this link to your IT admin:
          </Typography>
          <Box
            sx={{
              p: 1.5,
              // grey.100 is always light — unreadable with dark-mode text; use dark surface when palette is dark
              bgcolor:
                muiTheme.palette.mode === 'dark'
                  ? muiTheme.palette.grey[800]
                  : muiTheme.palette.grey[100],
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              mb: 2,
              overflow: 'hidden',
              wordBreak: 'break-all',
              overflowWrap: 'break-word',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                m: 0,
                display: 'block',
                wordBreak: 'break-all',
                overflowWrap: 'break-word',
                whiteSpace: 'normal',
                color: 'text.primary',
              }}
            >
              {getOffice365AdminConsentUrl()}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              onClick={() => {
                navigator.clipboard.writeText(getOffice365AdminConsentUrl());
                setAdminConsentCopySuccess(true);
                setTimeout(() => setAdminConsentCopySuccess(false), 2500);
              }}
            >
              {adminConsentCopySuccess ? 'Copied!' : 'Copy link'}
            </Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAdminConsentDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              setAdminConsentDialogOpen(false);
              handleOffice365OAuth();
            }}
            startIcon={
              <Box component="img" src="/images/apps/outlook-v2.png" alt="" sx={{ width: 20, height: 20 }} />
            }
          >
            Connect Office 365
          </Button>
        </DialogActions>
      </Dialog>

      {/* Messaging Provider Connection Modal - z-index above workspace dropdown so it never overlaps */}
      <Dialog
        open={messagingProviderModalOpen}
        onClose={handleCloseMessagingProviderModal}
        maxWidth="sm"
        fullWidth
        sx={{ zIndex: 1400 }}
        PaperProps={{
          sx: {
            ...modalStyles.dialog['& .MuiDialog-paper']
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {googleChatPickerOpen ? 'Connect Google Chat' :
           selectedProviderForModal === 'whatsapp' ? 'Connect WhatsApp' :
           selectedProviderForModal === 'slack' ? 'Connect Slack' :
           selectedProviderForModal === 'google_chat' ? 'Connect Google Chat Space' :
           selectedProviderForModal === 'google_chat_inbox' ? 'Connect Google Chat Direct Messages' :
           selectedProviderForModal === 'teams' ? 'Connect Microsoft Teams' : 'Connect Messaging Provider'}
          <IconButton onClick={handleCloseMessagingProviderModal} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
            {error && (
              <Alert severity="error">{error}</Alert>
            )}

            {/* Google Chat mode picker — Space vs. Direct Messages. Shown instead of any
                provider's fields until a mode is chosen, at which point selectedProviderForModal
                resolves to the real underlying provider and everything below behaves exactly as
                it already did before the tiles were merged. */}
            {googleChatPickerOpen && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  How should Prism7 connect to Google Chat?
                </Typography>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => handleSelectGoogleChatMode('google_chat')}
                  sx={{ py: 2, justifyContent: 'flex-start', textAlign: 'left', textTransform: 'none' }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>Space</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Connect a shared Google Chat Space using a service account and Space ID.
                    </Typography>
                  </Box>
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => handleSelectGoogleChatMode('google_chat_inbox')}
                  sx={{ py: 2, justifyContent: 'flex-start', textAlign: 'left', textTransform: 'none' }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>Direct Messages</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Sign in as a real Google Workspace mailbox (e.g. hrsupport@yourcompany.com) and
                      answer its Direct Messages.
                    </Typography>
                  </Box>
                </Button>
              </Box>
            )}

            {!googleChatPickerOpen && !messagingConnectionTested && (
              <>
                {/* WhatsApp Fields */}
                {selectedProviderForModal === 'whatsapp' && (
                  <>
                    <Box>
                      <Typography variant="body2" component="label" sx={{ fontWeight: 500, display: 'block', mb: 1 }}>
                        Access Token
                        <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>*</Box>
                      </Typography>
                      <TextField
                        fullWidth
                        type="password"
                        value={messagingProviderCredentials.whatsapp.access_token}
                        onChange={(e) => handleMessagingProviderCredentialsChange('access_token', e.target.value)}
                        placeholder="EAAxxxxxxxxxxxx"
                        required
                      />
                      <Typography variant="caption" sx={{ mt: 0.5, display: 'block' }}>
                        <Link
                          href="https://developers.facebook.com/documentation/business-messaging/whatsapp/access-tokens"
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ color: 'primary.main', fontSize: '0.75rem' }}
                        >
                          Learn how to get your Access Token
                        </Link>
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" component="label" sx={{ fontWeight: 500, display: 'block', mb: 1 }}>
                        Phone Number ID
                        <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>*</Box>
                      </Typography>
                      <TextField
                        fullWidth
                        value={messagingProviderCredentials.whatsapp.phone_number_id}
                        onChange={(e) => handleMessagingProviderCredentialsChange('phone_number_id', e.target.value)}
                        placeholder="123456789012345"
                        required
                      />
                      <Typography variant="caption" sx={{ mt: 0.5, display: 'block' }}>
                        <Link
                          href="https://developers.facebook.com/docs/whatsapp/cloud-api/get-started#get-phone-number-id"
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ color: 'primary.main', fontSize: '0.75rem' }}
                        >
                          Learn how to get your Phone Number ID
                        </Link>
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <TextField
                        fullWidth
                        label="Sync Interval"
                        type="number"
                        value={messagingProviderCredentials.whatsapp.sync_interval}
                        onChange={(e) => handleMessagingProviderCredentialsChange('sync_interval', parseInt(e.target.value) || 30)}
                        required
                        sx={{ flex: 2 }}
                      />
                      <FormControl fullWidth sx={{ flex: 1 }}>
                        <InputLabel>Unit</InputLabel>
                        <Select
                          value={messagingProviderCredentials.whatsapp.sync_interval_unit || 'seconds'}
                          label="Unit"
                          onChange={(e) => handleMessagingProviderCredentialsChange('sync_interval_unit', e.target.value)}
                        >
                          <MenuItem value="seconds">Seconds</MenuItem>
                          <MenuItem value="minutes">Minutes</MenuItem>
                          <MenuItem value="hours">Hours</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  </>
                )}

                {/* Slack Fields */}
                {selectedProviderForModal === 'slack' && (
                  <>
                    <TextField
                      fullWidth
                      label="Bot Token"
                      type="password"
                      value={messagingProviderCredentials.slack.bot_token}
                      onChange={(e) => handleMessagingProviderCredentialsChange('bot_token', e.target.value)}
                      placeholder="xoxb-xxxxxxxxxxxx"
                      required
                    />
                    <TextField
                      fullWidth
                      label="Workspace ID (Optional)"
                      value={messagingProviderCredentials.slack.workspace_id}
                      onChange={(e) => handleMessagingProviderCredentialsChange('workspace_id', e.target.value)}
                      placeholder="T1234567890"
                    />
                  </>
                )}

                {/* Google Chat Fields */}
                {selectedProviderForModal === 'google_chat' && (
                  <>
                    {/* Hidden file input — triggered by the upload button */}
                    <input
                      ref={gchatFileInputRef}
                      type="file"
                      accept=".json,application/json"
                      style={{ display: 'none' }}
                      onChange={handleGchatKeyFileUpload}
                    />

                    {/* Service Account Key — upload area */}
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Service Account Key (JSON) *
                        </Typography>
                        <Tooltip
                          title={
                            <Box>
                              <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                                Get your key from Google Cloud Console:
                              </Typography>
                              <Link
                                href="https://cloud.google.com/iam/docs/creating-managing-service-account-keys"
                                target="_blank"
                                rel="noopener noreferrer"
                                variant="caption"
                                sx={{ color: 'primary.light' }}
                              >
                                IAM &amp; Admin → Service Accounts → Keys
                              </Link>
                            </Box>
                          }
                          arrow
                          placement="right"
                        >
                          <InfoOutlinedIcon
                            sx={{ fontSize: 16, color: 'text.secondary', cursor: 'default' }}
                          />
                        </Tooltip>
                      </Box>

                      {/* Upload drop zone */}
                      <Box
                        onClick={() => gchatFileInputRef.current?.click()}
                        sx={{
                          border: '2px dashed',
                          borderColor: gchatKeyFileName ? 'success.main' : 'divider',
                          borderRadius: 1,
                          p: 2.5,
                          textAlign: 'center',
                          cursor: 'pointer',
                          transition: 'border-color 0.2s, background-color 0.2s',
                          backgroundColor: gchatKeyFileName
                            ? (theme) => alpha(theme.palette.success.main, 0.06)
                            : 'transparent',
                          '&:hover': {
                            borderColor: 'primary.main',
                            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04),
                          },
                        }}
                      >
                        <CloudUploadIcon
                          sx={{
                            fontSize: 36,
                            color: gchatKeyFileName ? 'success.main' : 'text.secondary',
                            mb: 0.5,
                          }}
                        />
                        {gchatKeyFileName ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="body2" color="success.main" sx={{ fontWeight: 500 }}>
                              {gchatKeyFileName}
                            </Typography>
                            <Button
                              size="small"
                              color="error"
                              variant="outlined"
                              onClick={(e) => { e.stopPropagation(); handleGchatKeyClear(); }}
                              sx={{ minWidth: 0, px: 1, py: 0.25, fontSize: '0.7rem' }}
                            >
                              Remove
                            </Button>
                          </Box>
                        ) : (
                          <>
                            <Typography variant="body2" color="text.secondary">
                              Click to upload your service account JSON file
                            </Typography>
                            <Typography variant="caption" color="text.disabled">
                              .json files only
                            </Typography>
                          </>
                        )}
                      </Box>

                    </Box>

                    <TextField
                      fullWidth
                      label="Space ID"
                      value={messagingProviderCredentials.google_chat.space_id}
                      onChange={(e) => handleMessagingProviderCredentialsChange('space_id', e.target.value)}
                      placeholder="spaces/xxxxxxxxxxxx"
                      required
                    />
                  </>
                )}

                {/* Google Chat Inbox Fields — DM-only, OAuth2 real-mailbox provider. Mirrors the
                    Gmail connect pattern exactly: no pre-typed field, the mailbox address comes
                    from whichever account is chosen in the OAuth popup (see the handleOAuthData
                    listener's google_chat_inbox branch), shown here only as post-connect
                    confirmation. See docs/GoogleChatInbox_DM_FE_Implementation.md §5.3. */}
                {selectedProviderForModal === 'google_chat_inbox' && (
                  <>
                    {messagingProviderCredentials.google_chat_inbox.access_token ? (
                      <Alert severity="success" icon={<CheckCircle />}>
                        Connected as {messagingProviderCredentials.google_chat_inbox.mailbox_email}.
                      </Alert>
                    ) : (
                      <>
                        <Button
                          variant="outlined"
                          fullWidth
                          disabled={gchatInboxOauthLoading}
                          onClick={handleGoogleChatInboxOAuth}
                          startIcon={
                            gchatInboxOauthLoading ? (
                              <CircularProgress size={16} />
                            ) : (
                              <Box
                                component="img"
                                src="/images/apps/gchat.png"
                                alt=""
                                sx={{ width: 20, height: 20, objectFit: 'contain', display: 'block' }}
                              />
                            )
                          }
                        >
                          {gchatInboxOauthLoading ? 'Connecting…' : 'Sign in to connect'}
                        </Button>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          Sign in as the real Google Workspace mailbox (e.g. hrsupport@yourcompany.com)
                          you want Prism7 to answer Direct Messages for.
                        </Typography>
                      </>
                    )}
                  </>
                )}

                {/* Teams Fields */}
                {selectedProviderForModal === 'teams' && (
                  <>
                    <TextField
                      fullWidth
                      label="Client ID"
                      value={messagingProviderCredentials.teams.client_id}
                      onChange={(e) => handleMessagingProviderCredentialsChange('client_id', e.target.value)}
                      placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                      required
                    />
                    <TextField
                      fullWidth
                      label="Client Secret"
                      type="password"
                      value={messagingProviderCredentials.teams.client_secret}
                      onChange={(e) => handleMessagingProviderCredentialsChange('client_secret', e.target.value)}
                      placeholder="xxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                      required
                    />
                    <TextField
                      fullWidth
                      label="Tenant ID"
                      value={messagingProviderCredentials.teams.tenant_id}
                      onChange={(e) => handleMessagingProviderCredentialsChange('tenant_id', e.target.value)}
                      placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                      required
                    />
                  </>
                )}

              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMessagingProviderModal} disabled={testingMessagingConnection}>
            Close
          </Button>
          {/* No "Test Connection" button while the Google Chat picker is showing (nothing chosen
              yet) or for google_chat_inbox — that flow's OAuth popup completing (handled by
              handleGoogleChatInboxOAuth + the handleOAuthData listener) IS the connection test;
              POST /messaging/test-connection doesn't support this provider. */}
          {!googleChatPickerOpen && selectedProviderForModal !== 'google_chat_inbox' && (
            <Button
              variant="contained"
              onClick={handleTestMessagingConnection}
              disabled={testingMessagingConnection}
              startIcon={testingMessagingConnection ? <CircularProgress size={16} /> : null}
            >
              {testingMessagingConnection ? 'Testing...' : 'Test Connection'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <DialogActions sx={{ p: 3, pt: 0 }} data-testid="add-space-modal-actions">
        {/* Show Cancel button when showing channel type selection */}
        {showChannelTypeSelection && (
          <Button
            data-testid="add-space-modal-cancel-button-channel-type"
            onClick={handleClose}
            sx={{ color: 'text.secondary' }}
          >
            Cancel
          </Button>
        )}

        {/* Show buttons for step creation */}
        {showStepCreation && (
          <>
            {/* Cancel button - shown on all steps */}
            <Button
              data-testid={`add-space-modal-cancel-button-step-${activeStep}`}
              onClick={handleClose}
              sx={{ color: 'text.secondary' }}
            >
              Cancel
            </Button>

            {/* Action buttons (Back, Skip, Next/Complete Setup) */}
            <Box sx={{ display: 'flex', gap: 1 }} data-testid={`add-space-modal-action-buttons-step-${activeStep}`}>
              {/* Back button - shown on all steps except step 0 */}
              {activeStep > 0 && (
                <Button
                  data-testid={`add-space-modal-back-button-step-${activeStep}`}
                  onClick={handleBack}
                  startIcon={<ArrowBack />}
                  variant="outlined"
                  sx={{ color: 'text.secondary' }}
                  disabled={dataSourceLoading && isOnDataSourceStep}
                >
                  Back
                </Button>
              )}

              {/* Skip button - shown for specific steps (email: step 1 & 2, message: step 1) */}
              {shouldShowSkipButton() && (
                <Button
                  data-testid={`add-space-modal-skip-button-step-${activeStep}`}
                  onClick={handleSkip}
                  variant="outlined"
                  sx={{ color: 'text.secondary' }}
                  disabled={dataSourceLoading && isOnDataSourceStep}
                >
                  Skip
                </Button>
              )}

              {/* Next or Complete Setup button */}
              {activeStep === steps.length - 1 ? (
                // Last step: Show Complete Setup
                <Button
                  data-testid="add-space-modal-complete-setup-button"
                  onClick={handleCreateSpace}
                  variant="contained"
                  color="primary"
                >
                  Complete Setup
                </Button>
              ) : (
                // Not last step: Show Next
                <Button
                  data-testid={`add-space-modal-next-button-step-${activeStep}`}
                  onClick={handleNext}
                  endIcon={
                    isStep0EmailRulesValidating ||
                    (loading && activeStep === 0) ||
                    (dataSourceLoading && isOnDataSourceStep)
                      ? <CircularProgress size={16} />
                      : <ArrowForward />
                  }
                  variant="contained"
                  disabled={
                    (!isStepValid(activeStep) && activeStep === 0) ||
                    loading ||
                    (dataSourceLoading && isOnDataSourceStep) ||
                    hasBlockingBusinessRulesValidation() ||
                    // channel_business_rules is only required by the backend on new-channel
                    // creation — exempt once createdChannelId exists (reconnect/update path).
                    (selectedChannelType === 'email' && activeStep === 0 && !createdChannelId && spaceData.description.trim() !== '' && (isBusinessRulesSuggesting || businessRules.trim() === '')) ||
                    (selectedChannelType === 'message' && activeStep === 0 && !createdChannelId && !messagingConnectionData?.channel_id && spaceData.description.trim() !== '' && businessRules.trim() === '')
                  }
                >
                  {isStep0EmailRulesValidating
                    ? 'Validating business rules...'
                    : loading && activeStep === 0
                    ? 'Creating...'
                    : dataSourceLoading && isOnDataSourceStep
                    ? 'Uploading...'
                    : 'Next'}
                </Button>
              )}
            </Box>
          </>
        )}
      </DialogActions>

      {/* Odoo #17929 — onboarding preview/edit of the auto-created channel's workflow canvas */}
      <WorkflowCanvasPreviewDialog
        open={showWorkflowPreview}
        onClose={() => setShowWorkflowPreview(false)}
        channelId={createdChannelId}
        channelBusinessRules={businessRules}
        onBusinessRulesChanged={(updatedRules) => {
          // PR #650 review (C1) — the canvas persists node edits to the backend itself and
          // reports the resulting text here; without this, AddSpaceModal's own businessRules
          // state stays stale, and the "channel already exists" PUT in handleNext
          // (getBusinessRulesSaveExtras()) would silently overwrite the just-made edit.
          setBusinessRules(updatedRules);
          if (businessRulesServerError) setBusinessRulesServerError('');
        }}
      />

      {/* Signature & Salutation Modal */}
      <AddSignatureAndSalutationModal
        open={showSignatureModal}
        onClose={() => setShowSignatureModal(false)}
        channelId={createdChannelId}
        onSave={(data) => {
          const updated = [...spaceData.signatureAndSalutation, data];
          handleSpaceDataChange('signatureAndSalutation', updated);
        }}
      />

      <Dialog
        open={isDescriptionExpanded}
        onClose={() => setIsDescriptionExpanded(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: '90vh',
            bgcolor: 'background.paper',
            color: 'text.primary',
          },
        }}
        sx={{ zIndex: 9999 }}
      >
        <DialogTitle sx={modalStyles.dialogTitle}>
          <Typography variant="h6" component="span">
            Purpose of the Channel
          </Typography>
          <IconButton
            onClick={() => setIsDescriptionExpanded(false)}
            size="small"
            aria-label="Close expanded description"
            sx={modalStyles.closeButton}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={(theme) => ({
            bgcolor: 'background.paper',
            paddingTop: `${theme.spacing(3)} !important`,
          })}
        >
          <TextField
            fullWidth
            margin="normal"
            autoFocus
            label="Description"
            value={spaceData.description}
            onChange={(e) => handleSpaceDataChange('description', e.target.value)}
            placeholder="e.g., This channel is for the HR support shared email inbox"
            multiline
            minRows={14}
            required
            InputLabelProps={{
              required: false,
              shrink: true,
              sx: {
                bgcolor: 'background.paper',
                px: 0.5,
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, bgcolor: 'background.paper' }}>
          <Button onClick={() => setIsDescriptionExpanded(false)} variant="contained">
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default AddSpaceModal;
